import React, {
  ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Autocomplete,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  TextField,
} from "@mui/material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import LockResetIcon from "@mui/icons-material/LockReset";
import PauseCircleOutlineIcon from "@mui/icons-material/PauseCircleOutline";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import PreviewOutlinedIcon from "@mui/icons-material/PreviewOutlined";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import {
  AffinityProgramPartnerActivationStatusEnum,
  ChamberMemberCategory,
  ChamberMemberInput,
  ChamberMemberProMembershipModelPaymentResponsibilityEnum,
  ChamberMemberProMembershipPricingModelEnum,
  ChamberMemberProPaymentScheduleEnum,
  ChangeCmpMemberMembershipMutation,
  CmpInvoiceStatusEnum,
  CmpMemberMembershipChangeOutcomeEnum,
  CmpMemberDetailFieldsFragment,
  CmpMemberLifecycleStatusEnum,
  CmpPaymentChoiceTypeEnum,
  GetCmpMemberDetailQuery,
  LoginInfoEmailTypes,
  SendCmpMemberMembershipChangeInvoiceMutation,
  useActivateCmpMemberMutation,
  useApproveCmpMemberBoardMutation,
  useArchiveCmpMemberMutation,
  useChangeCmpMemberMembershipMutation,
  useGetAllAffinityProgramPartnersForChamberQuery,
  useGetAllCategoriesQuery,
  useGetChamberMemberProSettingsQuery,
  useGetCmpManageMembersQuery,
  useGetCmpMemberDetailQuery,
  useGetSampleLoginInfoEmailQuery,
  useResendCmpMemberPaymentRequestMutation,
  useSendCmpMemberMembershipChangeInvoiceMutation,
  useSendLoginInfoEmailToChamberMemberMutation,
  useSetNewTemporaryChamberMemberPasswordMutation,
  useStartCmpMemberApplicationFromExistingMutation,
  useSuspendCmpMemberMutation,
  useUpdateCmpMemberMutation,
} from "../../../../graphql/types";
import { RoutePaths } from "../../../../enumStrings";
import useGetChamberFromUserIdInAuth from "../../../../hooks/useGetChamberFromUserIdInAuth";
import { getCmpCreditCardPaymentsReady } from "../../../../utils/cmpCreditCardPayments";
import { usePageHeaderOverride } from "../../../Layout/PageContainer";
import UnsavedChangesPrompt from "../../../common/UnsavedChangesPrompt";
import { downloadTextFile } from "../../Invoicing/invoiceFileDownloads";
import MemberBillingStep, {
  chamberGroupInsuranceExistingInvoiceDescription,
  MemberBillingErrors,
  MemberBillingOption,
} from "../MemberBillingStep";
import {
  getMembershipBasePrice,
  getMembershipInvoiceTaxRatePercentage,
} from "../memberMembershipPricing";
import {
  cmpLongDateFormatter,
  formatUtcCalendarDate,
} from "../../../common/dashboard/formatters";
import {
  buildProfileUpdateMembershipInput,
  getMemberMembershipPresentation,
} from "../memberProfileMembership";
import { commonStyles } from "../../../common/commonStyles";
import { wizardGoldButtonSx } from "../../../common/dashboard/formStyles";
import {
  checkboxControlSx,
  choiceControlLabelSx,
  consumeUpdateMemberNavigationMemberId,
  cpaGold,
  cpaGoldDark,
  fieldSx,
  FormCalendarDateField,
  FormMultiSelectField,
  FormSelectField,
  FormTextField,
  getUpdateMemberNavigationMemberId,
  getLogoInputValue,
  LogoUploadField,
  MemberStepProgress,
  memberWorkflowSteps,
  MultiOptionGroup,
  radioControlSx,
  SectionShell,
} from "../memberWorkflowShared";
import {
  ContactRole,
  MemberContactInfo,
  MemberCustomField,
  MemberStatus,
} from "../mockMemberData";

type BackendMemberDetail = GetCmpMemberDetailQuery["getCmpMemberDetail"];

type BillingResponsibilityOption =
  | "memberPays"
  | "affiliatedPrimaryCompany"
  | "chamberGroupInsurance"
  | "complimentary"
  | "freeLifetime";

type PaymentMethodType =
  | "creditCard"
  | "free"
  | "invoice"
  | "cash"
  | "cheque"
  | "bankTransfer"
  | "eTransfer";

type ReferralTrackingInfo = {
  id: string;
  referralDate: string;
  becameMemberDate: string;
  companyReferralName: string;
  contactName: string;
  email: string;
  contactNumber: string;
  contactNumberExtension: string;
};

type AffinityProgramEnrollment = {
  id: string;
  affinityProgramPartnerId: string;
  affinityProgramPartnerName: string;
  idNumber: string;
  startDate: string;
  billingOption: string;
  status: string;
  notes: string;
};

type UpdateMemberFormState = {
  association: {
    isAssociateCompanyMember: boolean;
    primaryMemberCompanyId: string;
  };
  membership: {
    membershipTypeId: string;
    fullTimeEmployees: string;
    partTimeEmployees: string;
    paymentSchedule: ChamberMemberProPaymentScheduleEnum | "";
    billingResponsibility: BillingResponsibilityOption;
    paymentMethod: PaymentMethodType | "";
    selectedAdditionalCostIds: string[];
    membershipDiscount: {
      enabled: boolean;
      code: string;
      description: string;
      amount: string;
    };
    autoRenew: boolean;
    emailCommunicationConsentConfirmed: boolean;
  };
  companyDetails: {
    yearEstablished: string;
    howDidYouHearAboutUs: string[];
    howDidYouHearAboutUsOther: string;
    primaryReasonsForJoining: string[];
    primaryReasonForJoiningOther: string;
    activeSocialPlatforms: string[];
    boardOrCommitteeInterest: string;
  };
  insights: {
    chamberPerksAppHeardAbout: string;
    chamberPerksAppDownloaded: string;
    boardDirectorInterest: string;
    committeeInterest: string;
    communicationPreferences: string[];
  };
  referralTracking: ReferralTrackingInfo[];
  affiliatedChamberMember: {
    isAffiliatedChamberMember: boolean;
    chamberName: string;
  };
  affinityProgramEnrollments: AffinityProgramEnrollment[];
  directory: {
    companyName: string;
    companyDescription: string;
    companyWebsite: string;
    googleReviewUrl: string;
    mainCompanyContactName: string;
    mainCompanyContactEmail: string;
    mainCompanyPhone: string;
    mainCompanyPhoneExt: string;
    directoryCategoryIds: ChamberMemberCategory[];
    showInDirectory: boolean;
  };
  address: {
    hideStreetAddressInDirectories: boolean;
    streetAddress: string;
    city: string;
    provinceOrState: string;
    postalOrZipCode: string;
    country: string;
  };
  socialLinks: {
    facebookUrl: string;
    instagramUrl: string;
    threadsUrl: string;
    linkedinUrl: string;
    xTwitterUrl: string;
    tikTokUrl: string;
    youtubeUrl: string;
  };
  contacts: Record<ContactRole, MemberContactInfo>;
  // Persisted "same as" checkbox state for each contact card. Stored in
  // extension.applicationMetadata so the checkboxes stay checked after
  // save/reload (MEM-UPDATE-005).
  contactSameAs: Record<ContactRole, boolean>;
  billingAddress: {
    streetAddress: string;
    city: string;
    provinceOrState: string;
    postalOrZipCode: string;
    country: string;
  };
  profile: {
    internalNotes: string;
    customFields: MemberCustomField[];
  };
  logo: {
    fileName: string;
    previewUrl: string;
  };
};

type MembershipFormState = UpdateMemberFormState["membership"];

export type UpdateMemberMembershipAdjustmentInput = {
  chamberId: string;
  memberId: string;
  membership: Omit<
    MembershipFormState,
    "selectedAdditionalCostIds" | "emailCommunicationConsentConfirmed"
  >;
  operationId: string;
  expectedMemberUpdatedAtUtcMilli: number;
  expectedCurrentInvoiceId?: string;
};

export type UpdateMemberMembershipAdjustmentResult =
  ChangeCmpMemberMembershipMutation["changeCmpMemberMembership"];

export type UpdateMemberMembershipChangeInvoiceSendInput = {
  chamberId: string;
  memberId: string;
  invoiceId: string;
  operationId: string;
};

export type UpdateMemberMembershipChangeInvoiceSendResult =
  SendCmpMemberMembershipChangeInvoiceMutation["sendCmpMemberMembershipChangeInvoice"];

export type UpdateMemberProps = {
  /**
   * Dedicated membership-adjustment boundary. The persisted add-on IDs are
   * deliberately absent from the input so this action cannot recharge them.
   */
  onMembershipAdjustmentSubmit?: (
    input: UpdateMemberMembershipAdjustmentInput
  ) => Promise<UpdateMemberMembershipAdjustmentResult>;
  /**
   * Email-only boundary for the invoice already committed by the membership
   * change. It must never create, replace, or reprice an invoice.
   */
  onMembershipChangeInvoiceSend?: (
    input: UpdateMemberMembershipChangeInvoiceSendInput
  ) => Promise<UpdateMemberMembershipChangeInvoiceSendResult>;
};

type ConfirmationModal =
  | "resetPassword"
  | "suspend"
  | "activate"
  | "unsuspend"
  | "delete"
  | "restartApplication"
  | null;

type CredentialModalMode = "sendCredentials" | "resetPassword" | null;

type StatusMessage = {
  severity: "success" | "info" | "warning" | "error";
  message: string;
};

const shouldMarkCmpModuleTestData = process.env.NODE_ENV === "development";
const descriptionMaxLength = 2000;

const hearAboutOptions = [
  "Member Referral",
  "Chamber Website",
  "Chamber Perks App",
  "Chamber Event",
  "Chamber Staff",
  "Social Media",
  "Other",
];

const reasonForJoiningOptions = [
  "Cost Saving Programs and Perks",
  "Networking Opportunities",
  "Advocacy and Resources",
  "Chamber Events",
  "Community Involvement",
  "Other",
];

const socialPlatformOptions = [
  "LinkedIn",
  "Twitter/X",
  "Facebook",
  "Instagram",
  "TikTok",
  "YouTube",
  "None",
];

const yesNoOptions = ["Yes", "No"];
// Interest rows store the affirmative sentence itself (mirrors Create step 3's
// radio matrix per the step 3 screenshot spec); "board of director" and "a
// committees" mock typos are corrected.
const boardDirectorInterestAffirmative =
  "Interested in becoming a chamber board director";
const committeeInterestAffirmative = "Interested in joining a committee";

// Legacy records saved "Yes" from the old dropdown control; map it onto the
// affirmative radio value so the selection still shows.
const normalizeInterestValue = (
  value: string | null | undefined,
  affirmativeValue: string
) => {
  const storedValue = value || "";

  if (!storedValue) {
    return "";
  }

  return storedValue === "No" || storedValue === "Maybe"
    ? storedValue
    : affirmativeValue;
};

// Year established is intentionally year-only. Older dashboard builds stored
// the value as yyyy-01-01, so extract the year while hydrating those records.
const toEstablishedYearValue = (value?: string | null) => {
  const trimmedValue = (value || "").trim();
  const yearMatch = trimmedValue.match(/\d{4}/);
  return yearMatch ? yearMatch[0] : "";
};

const isValidEstablishedYear = (value: string) => {
  if (!value) {
    return true;
  }

  const year = Number(value);
  return /^\d{4}$/.test(value) && year >= 1000 && year <= new Date().getFullYear();
};

// Display formatter for native date-input values ("yyyy-mm-dd"). Parse the
// parts locally instead of new Date(string) so the rendered day never shifts
// a day backwards in negative-UTC-offset timezones.
const formatDateInputValueLong = (value?: string) => {
  if (!value) {
    return "-";
  }

  const [year, month, day] = value.split("-").map(Number);

  if (!year || !month || !day) {
    return value;
  }

  return cmpLongDateFormatter.format(new Date(year, month - 1, day));
};

const communicationPreferenceOptions = [
  "I prefer communication from the chamber to be done via phone",
  "I prefer communication from the chamber to be done via text message",
  "I prefer communication from the chamber to be done via email. Member is aware they can unsubscribe at any time.",
];

const affinityBillingOptions = [
  "(DO NOT INVOICE MEMBER) Affinity Partner pays chamber monthly for member membership",
  "Affinity Partner invoices member separately",
  "Included with chamber membership",
];

const affinityDoNotInvoicePrefix = "(DO NOT INVOICE MEMBER)";

// Local mirror of CreateMember's renderAffinityBillingOptionLabel — keep the
// two in sync. The step 3 mock bolds the "(DO NOT INVOICE MEMBER)"
// parenthetical wherever this billing option appears (draft Billing Option
// select, Enrolled Affinity Programs table cell, Update-modal radios).
const renderAffinityBillingOptionLabel = (option: string) =>
  option.startsWith(affinityDoNotInvoicePrefix) ? (
    <>
      <strong>{affinityDoNotInvoicePrefix}</strong>
      {option.slice(affinityDoNotInvoicePrefix.length)}
    </>
  ) : (
    option
  );

const affinityEnrollmentStatusOptions = ["Active", "Inactive"];
const chamberGroupInsurancePaymentMarker =
  "Paid by Johnson Group (Chamber Insurance Plan)";

const membershipBillingOptions: {
  value: BillingResponsibilityOption;
  title: string;
  description: string;
  descriptionBold?: boolean;
}[] = [
  {
    value: "memberPays",
    title: "Member will pay",
    description: "Invoice to be sent to member for payment",
  },
  {
    value: "affiliatedPrimaryCompany",
    title: "Invoice Affiliated Primary Company",
    description:
      "Member has requested that invoice be sent to the primary company for payment",
  },
  // Step-4 spec marks the three internal-use sub-lines below as bold.
  {
    value: "chamberGroupInsurance",
    title: chamberGroupInsurancePaymentMarker,
    description: chamberGroupInsuranceExistingInvoiceDescription,
    descriptionBold: true,
  },
  {
    value: "complimentary",
    title: "Complimentary No Charge Membership (For promotion/contra)",
    description: "Invoice created for Internal Use Not Sent to Member",
    descriptionBold: true,
  },
  {
    value: "freeLifetime",
    title: "Free Lifetime Membership",
    description: "Invoice created for Internal Use Not Sent to Member",
    descriptionBold: true,
  },
];

const provinceOptions = [
  "Alberta",
  "British Columbia",
  "Manitoba",
  "New Brunswick",
  "Newfoundland and Labrador",
  "Nova Scotia",
  "Ontario",
  "Prince Edward Island",
  "Quebec",
  "Saskatchewan",
];

const contactSections: Array<{
  role: ContactRole;
  title: string;
  description: string;
  sameAsLabel: string;
  nameLabel: string;
  phoneLabel: string;
  requiredFields: Array<"name" | "email">;
}> = [
  {
    role: "primaryVoting",
    title: "Member Primary Contact (Voting Member)",
    description: "Enter the Primary Contact/Voting Member details below.",
    sameAsLabel:
      "Check here if same as Main Contact (Public on all Chamber Directories)",
    nameLabel: "Primary Contact Name",
    phoneLabel: "Contact Number",
    requiredFields: ["name", "email"],
  },
  {
    role: "administrator",
    title: "Member Administrator Contact",
    description:
      "Enter the designated Administrator Contact who will receive this member's login credentials and be responsible for managing their Member Dashboard, updating their member directory listing, accessing the Chamber Perks App™, and managing their member perks, advertising, and promotional opportunities.",
    sameAsLabel: "Check here if same as Primary Contact",
    nameLabel: "Administrator Contact Name",
    phoneLabel: "Contact Number",
    requiredFields: ["name", "email"],
  },
  {
    role: "accountsPayable",
    title: "Accounts Payable Contact and Billing Address",
    description:
      "Enter the contact responsible for managing payments for this member, and the billing address.",
    sameAsLabel: "Check here if same as Primary Company Contact",
    nameLabel: "Billing Contact Name",
    phoneLabel: "Contact Number",
    requiredFields: ["name", "email"],
  },
  {
    role: "employeeAdoption",
    title: "Contact for Employee Adoption of the Chamber Perks App™ and Chamber Benefits",
    description:
      "Enter the contact who is the best person to share information internally to help this member and their employees maximize the value of their chamber membership and save money through the Chamber Perks App™.",
    sameAsLabel: "Check here if same as Primary Company Contact",
    nameLabel: "Company Contact Name",
    phoneLabel: "Company Contact Number",
    requiredFields: [],
  },
  {
    role: "marketingCommunication",
    title: "Marketing and Communication Contact",
    description:
      "Enter the contact responsible for the member's marketing and communications to ensure Chamber promotions and sponsorship opportunities reach the right person and help members maximize the value of their membership.",
    sameAsLabel: "Check here if same as Primary Company Contact",
    nameLabel: "Contact Name",
    phoneLabel: "Company Contact Number",
    requiredFields: [],
  },
  {
    role: "hrEmployeeEngagement",
    title: "HR/Employee Engagement Contact",
    description:
      "Enter the contact to help promote and share chamber membership benefits internally with your employees, including cost-saving programs, events, resources, and the value of downloading the Chamber Perks App™ to save locally and across Canada.",
    sameAsLabel: "Check here if same as Primary Company Contact",
    nameLabel: "Contact Name",
    phoneLabel: "Contact Number",
    requiredFields: [],
  },
  {
    role: "sales",
    title: "Sales/Business Development Contact",
    description:
      "Enter the contact responsible for driving company growth and sales opportunities, and who would benefit from engagement and networking opportunities, partnerships, events, and initiatives that increase visibility and revenue through chamber programs.",
    sameAsLabel: "Check here if same as Primary Company Contact",
    nameLabel: "Contact Name",
    phoneLabel: "Contact Number",
    requiredFields: [],
  },
];

const statusLabels: Record<CmpMemberLifecycleStatusEnum, MemberStatus> = {
  [CmpMemberLifecycleStatusEnum.Active]: "Active",
  [CmpMemberLifecycleStatusEnum.Deleted]: "Past",
  [CmpMemberLifecycleStatusEnum.Past]: "Past",
  [CmpMemberLifecycleStatusEnum.Pending]: "Pending",
  [CmpMemberLifecycleStatusEnum.Prospect]: "Prospect",
  [CmpMemberLifecycleStatusEnum.Suspended]: "Suspended",
};

const lifecycleStatusByLabel: Record<MemberStatus, CmpMemberLifecycleStatusEnum> = {
  Active: CmpMemberLifecycleStatusEnum.Active,
  Past: CmpMemberLifecycleStatusEnum.Past,
  Pending: CmpMemberLifecycleStatusEnum.Pending,
  Prospect: CmpMemberLifecycleStatusEnum.Prospect,
  Suspended: CmpMemberLifecycleStatusEnum.Suspended,
};

const createLocalId = (prefix: string) =>
  `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

const createEmptyContact = (): MemberContactInfo => ({
  name: "",
  title: "",
  email: "",
  companyPhone: "",
  ext: "",
});

const createEmptyReferralTracking = (): ReferralTrackingInfo => ({
  id: createLocalId("referral"),
  referralDate: "",
  becameMemberDate: "",
  companyReferralName: "",
  contactName: "",
  email: "",
  contactNumber: "",
  contactNumberExtension: "",
});

const emptyFormState = (): UpdateMemberFormState => ({
  association: {
    isAssociateCompanyMember: false,
    primaryMemberCompanyId: "",
  },
  membership: {
    membershipTypeId: "",
    fullTimeEmployees: "",
    partTimeEmployees: "",
    paymentSchedule: "",
    billingResponsibility: "memberPays",
    paymentMethod: "",
    selectedAdditionalCostIds: [],
    membershipDiscount: {
      enabled: false,
      code: "",
      description: "",
      amount: "",
    },
    autoRenew: false,
    emailCommunicationConsentConfirmed: false,
  },
  companyDetails: {
    yearEstablished: "",
    howDidYouHearAboutUs: [],
    howDidYouHearAboutUsOther: "",
    primaryReasonsForJoining: [],
    primaryReasonForJoiningOther: "",
    activeSocialPlatforms: [],
    boardOrCommitteeInterest: "",
  },
  insights: {
    chamberPerksAppHeardAbout: "",
    chamberPerksAppDownloaded: "",
    boardDirectorInterest: "",
    committeeInterest: "",
    communicationPreferences: [],
  },
  referralTracking: [createEmptyReferralTracking()],
  affiliatedChamberMember: {
    isAffiliatedChamberMember: false,
    chamberName: "",
  },
  affinityProgramEnrollments: [],
  directory: {
    companyName: "",
    companyDescription: "",
    companyWebsite: "",
    googleReviewUrl: "",
    mainCompanyContactName: "",
    mainCompanyContactEmail: "",
    mainCompanyPhone: "",
    mainCompanyPhoneExt: "",
    directoryCategoryIds: [],
    showInDirectory: true,
  },
  address: {
    hideStreetAddressInDirectories: false,
    streetAddress: "",
    city: "",
    provinceOrState: "",
    postalOrZipCode: "",
    country: "Canada",
  },
  socialLinks: {
    facebookUrl: "",
    instagramUrl: "",
    threadsUrl: "",
    linkedinUrl: "",
    xTwitterUrl: "",
    tikTokUrl: "",
    youtubeUrl: "",
  },
  contacts: {
    primaryVoting: createEmptyContact(),
    administrator: createEmptyContact(),
    accountsPayable: createEmptyContact(),
    employeeAdoption: createEmptyContact(),
    marketingCommunication: createEmptyContact(),
    hrEmployeeEngagement: createEmptyContact(),
    sales: createEmptyContact(),
  },
  contactSameAs: {
    primaryVoting: false,
    administrator: false,
    accountsPayable: false,
    employeeAdoption: false,
    marketingCommunication: false,
    hrEmployeeEngagement: false,
    sales: false,
  },
  billingAddress: {
    streetAddress: "",
    city: "",
    provinceOrState: "",
    postalOrZipCode: "",
    country: "Canada",
  },
  profile: {
    internalNotes: "",
    customFields: [],
  },
  logo: {
    fileName: "",
    previewUrl: "",
  },
});

const cloneMembershipFormState = (
  membership: MembershipFormState
): MembershipFormState => ({
  ...membership,
  selectedAdditionalCostIds: [...membership.selectedAdditionalCostIds],
  membershipDiscount: { ...membership.membershipDiscount },
});

const getMembershipAdjustmentFingerprint = (
  membership: MembershipFormState
) =>
  JSON.stringify({
    membershipTypeId: membership.membershipTypeId,
    fullTimeEmployees: membership.fullTimeEmployees,
    partTimeEmployees: membership.partTimeEmployees,
    paymentSchedule: membership.paymentSchedule,
    billingResponsibility: membership.billingResponsibility,
    paymentMethod: membership.paymentMethod,
    membershipDiscount: membership.membershipDiscount,
    autoRenew: membership.autoRenew,
  });

const hasMembershipAdjustmentChanges = ({
  draft,
  persisted,
}: {
  draft: MembershipFormState | null;
  persisted: MembershipFormState;
}) =>
  !!draft &&
  getMembershipAdjustmentFingerprint(draft) !==
    getMembershipAdjustmentFingerprint(persisted);

const currencyFormatter = new Intl.NumberFormat("en-CA", {
  style: "currency",
  currency: "CAD",
});

const shortDateFormatter = cmpLongDateFormatter;

const formatCurrency = (amountInCents?: number | null) =>
  currencyFormatter.format((amountInCents || 0) / 100);

const formatTimestampDate = (utcMilli?: number | null) =>
  utcMilli ? shortDateFormatter.format(new Date(utcMilli)) : "-";

const toOptionalString = (value?: string | null) => {
  const trimmedValue = (value || "").trim();
  return trimmedValue ? trimmedValue : undefined;
};

const getNumber = (value: string) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const hasEmailShape = (value: string) => /^\S+@\S+\.\S+$/.test(value);

const getPaymentMethodFromChoice = (
  paymentChoice?: {
    type?: CmpPaymentChoiceTypeEnum | null;
    manualPaymentMethodId?: string | null;
  } | null
): PaymentMethodType | "" => {
  if (!paymentChoice) {
    return "";
  }

  if (paymentChoice.type === CmpPaymentChoiceTypeEnum.CreditCard) {
    return "creditCard";
  }

  if (paymentChoice.type === CmpPaymentChoiceTypeEnum.Free) {
    return "free";
  }

  const manualId = paymentChoice.manualPaymentMethodId || "";

  if (manualId === "INVOICE_ME") {
    return "invoice";
  }

  if (manualId === "CASH") {
    return "cash";
  }

  if (manualId === "DIRECT_DEPOSIT") {
    return "bankTransfer";
  }

  if (manualId === "E_TRANSFER") {
    return "eTransfer";
  }

  if (manualId === "CHEQUE") {
    return "cheque";
  }

  return "";
};

const getBillingResponsibilityFromMembership = (
  membership?: {
    paymentResponsibility?:
      | ChamberMemberProMembershipModelPaymentResponsibilityEnum
      | null;
    thirdPartyPayerName?: string | null;
    thirdPartyPaymentNotes?: string | null;
  } | null
): BillingResponsibilityOption => {
  if (
    membership?.paymentResponsibility ===
    ChamberMemberProMembershipModelPaymentResponsibilityEnum.NoCharge
  ) {
    return "complimentary";
  }

  if (
    membership?.paymentResponsibility ===
    ChamberMemberProMembershipModelPaymentResponsibilityEnum.ThirdPartyPays
  ) {
    if (
      membership.thirdPartyPayerName === chamberGroupInsurancePaymentMarker ||
      membership.thirdPartyPaymentNotes === chamberGroupInsurancePaymentMarker
    ) {
      return "chamberGroupInsurance";
    }

    return "affiliatedPrimaryCompany";
  }

  return "memberPays";
};

const getManualPaymentMethodId = (paymentMethod: PaymentMethodType | "") => {
  switch (paymentMethod) {
    case "invoice":
      return "INVOICE_ME";
    case "cash":
      return "CASH";
    case "bankTransfer":
      return "DIRECT_DEPOSIT";
    case "eTransfer":
      return "E_TRANSFER";
    case "cheque":
      return "CHEQUE";
    default:
      return undefined;
  }
};

const getPaymentChoiceType = (
  billingResponsibility: BillingResponsibilityOption,
  paymentMethod: PaymentMethodType | ""
) => {
  if (
    billingResponsibility === "complimentary" ||
    billingResponsibility === "freeLifetime"
  ) {
    return {
      type: CmpPaymentChoiceTypeEnum.Free,
    };
  }

  if (
    billingResponsibility === "affiliatedPrimaryCompany" ||
    billingResponsibility === "chamberGroupInsurance"
  ) {
    return {
      type: CmpPaymentChoiceTypeEnum.ThirdParty,
    };
  }

  if (paymentMethod === "creditCard") {
    return {
      type: CmpPaymentChoiceTypeEnum.CreditCard,
    };
  }

  if (paymentMethod === "free") {
    return {
      type: CmpPaymentChoiceTypeEnum.Free,
    };
  }

  return {
    type: CmpPaymentChoiceTypeEnum.Manual,
    manualPaymentMethodId: getManualPaymentMethodId(paymentMethod),
  };
};

const getEffectiveUpdatePaymentMethod = ({
  creditCardPaymentsReady,
  manualPaymentMethods,
  paymentMethod,
}: {
  creditCardPaymentsReady: boolean;
  manualPaymentMethods: Array<{ enabled: boolean; id: string }>;
  paymentMethod: PaymentMethodType | "";
}): PaymentMethodType | "" => {
  if (paymentMethod !== "creditCard" || creditCardPaymentsReady) {
    return paymentMethod;
  }

  const firstEnabledManualPaymentMethod = manualPaymentMethods.find(
    (method) => method.enabled
  );

  if (!firstEnabledManualPaymentMethod) {
    return "";
  }

  return getPaymentMethodFromChoice({
    type: CmpPaymentChoiceTypeEnum.Manual,
    manualPaymentMethodId: firstEnabledManualPaymentMethod.id,
  });
};

const getPaymentResponsibility = (
  billingResponsibility: BillingResponsibilityOption
) => {
  if (
    billingResponsibility === "complimentary" ||
    billingResponsibility === "freeLifetime"
  ) {
    return ChamberMemberProMembershipModelPaymentResponsibilityEnum.NoCharge;
  }

  if (
    billingResponsibility === "affiliatedPrimaryCompany" ||
    billingResponsibility === "chamberGroupInsurance"
  ) {
    return ChamberMemberProMembershipModelPaymentResponsibilityEnum.ThirdPartyPays;
  }

  return ChamberMemberProMembershipModelPaymentResponsibilityEnum.MemberPays;
};

const mapDetailToFormState = (
  detail: BackendMemberDetail | CmpMemberDetailFieldsFragment
): UpdateMemberFormState => {
  const formState = emptyFormState();
  const member = detail.member;
  const extension = member.cmpExtension;
  const membership = extension?.membership;
  const companyDetails = extension?.companyDetails;
  const applicationMetadata = extension?.applicationMetadata;
  const paymentChoice = membership?.paymentChoice || detail.currentInvoice?.paymentChoice;

  return {
    ...formState,
    association: {
      isAssociateCompanyMember:
        !!extension?.association?.isAssociateCompanyMember,
      primaryMemberCompanyId: extension?.association?.primaryMemberCompanyId || "",
    },
    membership: {
      membershipTypeId: membership?.membershipTypeId || "",
      fullTimeEmployees: String(membership?.fullTimeEmployees ?? ""),
      partTimeEmployees: String(membership?.partTimeEmployees ?? ""),
      paymentSchedule: membership?.paymentSchedule || "",
      billingResponsibility: getBillingResponsibilityFromMembership(membership),
      paymentMethod: getPaymentMethodFromChoice(paymentChoice),
      selectedAdditionalCostIds: membership?.selectedAdditionalCostIds || [],
      membershipDiscount: {
        enabled: !!membership?.membershipDiscount?.enabled,
        code: membership?.membershipDiscount?.code || "",
        description: membership?.membershipDiscount?.description || "",
        amount:
          membership?.membershipDiscount?.amountInCents === undefined ||
          membership?.membershipDiscount?.amountInCents === null
            ? ""
            : String(membership.membershipDiscount.amountInCents / 100),
      },
      autoRenew: !!paymentChoice?.autoRenew,
      emailCommunicationConsentConfirmed:
        !!paymentChoice?.emailCommunicationConsentConfirmed,
    },
    companyDetails: {
      yearEstablished: toEstablishedYearValue(companyDetails?.yearEstablished),
      howDidYouHearAboutUs: companyDetails?.howDidYouHearAboutUs || [],
      howDidYouHearAboutUsOther: companyDetails?.howDidYouHearAboutUsOther || "",
      primaryReasonsForJoining:
        companyDetails?.primaryReasonsForJoining || [],
      primaryReasonForJoiningOther:
        companyDetails?.primaryReasonForJoiningOther || "",
      activeSocialPlatforms: companyDetails?.activeSocialPlatforms || [],
      boardOrCommitteeInterest:
        companyDetails?.boardOrCommitteeInterest || "",
    },
    insights: {
      chamberPerksAppHeardAbout: extension?.chamberPerksAppHeardAbout || "",
      chamberPerksAppDownloaded: extension?.chamberPerksAppDownloaded || "",
      boardDirectorInterest: normalizeInterestValue(
        extension?.boardDirectorInterest,
        boardDirectorInterestAffirmative
      ),
      committeeInterest: normalizeInterestValue(
        extension?.committeeInterest,
        committeeInterestAffirmative
      ),
      communicationPreferences:
        applicationMetadata?.communicationPreferences || [],
    },
    referralTracking:
      extension?.referralTracking && extension.referralTracking.length > 0
        ? extension.referralTracking.map((referral) => ({
            id: referral.id || createLocalId("referral"),
            referralDate: referral.referralDate || "",
            becameMemberDate: referral.becameMemberDate || "",
            companyReferralName: referral.companyReferralName || "",
            contactName: referral.contactName || "",
            email: referral.email || "",
            contactNumber: referral.contactNumber || "",
            contactNumberExtension: referral.contactNumberExtension || "",
          }))
        : [createEmptyReferralTracking()],
    affiliatedChamberMember: {
      isAffiliatedChamberMember:
        !!extension?.affiliatedChamberMember?.isAffiliatedChamberMember,
      chamberName: extension?.affiliatedChamberMember?.chamberName || "",
    },
    affinityProgramEnrollments:
      extension?.affinityProgramEnrollments?.map((enrollment) => ({
        id: enrollment.id || createLocalId("affinity"),
        affinityProgramPartnerId: enrollment.affinityProgramPartnerId || "",
        affinityProgramPartnerName: enrollment.affinityProgramPartnerName || "",
        idNumber: enrollment.idNumber || "",
        startDate: enrollment.startDate || "",
        billingOption: enrollment.billingOption || "",
        status: enrollment.status || "",
        notes: enrollment.notes || "",
      })) || [],
    directory: {
      companyName: member.name || "",
      companyDescription: member.description || "",
      companyWebsite: member.website || "",
      googleReviewUrl: member.googleReviewLink || "",
      mainCompanyContactName: member.contactName || "",
      mainCompanyContactEmail: member.email || "",
      mainCompanyPhone: member.phoneNumber || "",
      mainCompanyPhoneExt: member.phoneNumberExtension || "",
      directoryCategoryIds: (member.categoryId || []) as ChamberMemberCategory[],
      showInDirectory: member.showInApp,
    },
    address: {
      hideStreetAddressInDirectories: !!member.hideAddressFromDirectory,
      streetAddress: member.address?.streetAddress || "",
      city: member.address?.city || "",
      provinceOrState: member.address?.province || "",
      postalOrZipCode: member.address?.postalCode || "",
      country: applicationMetadata?.country || "Canada",
    },
    socialLinks: {
      facebookUrl: member.facebook || "",
      instagramUrl: member.instagram || "",
      threadsUrl: member.threads || "",
      linkedinUrl: member.linkedin || "",
      xTwitterUrl: member.twitter || "",
      tikTokUrl: member.tiktok || "",
      youtubeUrl: member.youtube || "",
    },
    contacts: {
      primaryVoting: {
        name: member.primaryContactName || "",
        title: member.primaryContactTitle || "",
        email: member.primaryContactEmail || "",
        companyPhone: member.primaryContactPhoneNumber || "",
        ext: member.primaryContactPhoneNumberExtension || "",
      },
      administrator: {
        name: member.adminConsoleContactName || "",
        title: member.cpaAdminCompanyAdminContactTitle || "",
        email: member.adminConsoleLoginEmail || "",
        companyPhone: member.cpaAdminCompanyAdminContactNumber || "",
        ext: member.cpaAdminCompanyAdminContactNumberExtension || "",
      },
      accountsPayable: {
        name: extension?.accountsPayableContactName || "",
        title: extension?.accountsPayableContactTitle || "",
        email: extension?.accountsPayableContactEmail || "",
        companyPhone: extension?.accountsPayableContactNumber || "",
        ext: extension?.accountsPayableContactNumberExtension || "",
      },
      employeeAdoption: {
        name: member.cpaEmployeeAdoptionContactName || "",
        title: member.cpaEmployeeAdoptionContactTitle || "",
        email: member.cpaEmployeeAdoptionContactEmail || "",
        companyPhone: member.cpaEmployeeAdoptionContactNumber || "",
        ext: member.cpaEmployeeAdoptionContactNumberExtension || "",
      },
      marketingCommunication: {
        name: extension?.marketingCommunicationContactName || "",
        title: extension?.marketingCommunicationContactTitle || "",
        email: extension?.marketingCommunicationContactEmail || "",
        companyPhone: extension?.marketingCommunicationContactNumber || "",
        ext: extension?.marketingCommunicationContactNumberExtension || "",
      },
      hrEmployeeEngagement: {
        name: extension?.hrEmployeeEngagementContactName || "",
        title: extension?.hrEmployeeEngagementContactTitle || "",
        email: extension?.hrEmployeeEngagementContactEmail || "",
        companyPhone: extension?.hrEmployeeEngagementContactNumber || "",
        ext: extension?.hrEmployeeEngagementContactNumberExtension || "",
      },
      sales: {
        name: extension?.salesBusinessDevelopmentContactName || "",
        title: extension?.salesBusinessDevelopmentContactTitle || "",
        email: extension?.salesBusinessDevelopmentContactEmail || "",
        companyPhone: extension?.salesBusinessDevelopmentContactNumber || "",
        ext: extension?.salesBusinessDevelopmentContactNumberExtension || "",
      },
    },
    // The "same as" flags persist in applicationMetadata. The marketing/hr
    // aliases are legacy duplicates that CreateMember also writes, so they act
    // as fallbacks for records saved before the dedicated fields existed.
    contactSameAs: {
      primaryVoting:
        !!applicationMetadata?.primaryVotingSameAsMainCompanyContact,
      administrator: !!(
        applicationMetadata?.administratorSameAsCompanyPrimaryContact ??
        applicationMetadata?.marketingSameAsCompanyPrimaryContact
      ),
      accountsPayable:
        !!applicationMetadata?.accountsPayableSameAsCompanyPrimaryContact,
      employeeAdoption: !!(
        applicationMetadata?.employeeAdoptionSameAsCompanyPrimaryContact ??
        applicationMetadata?.hrSameAsCompanyPrimaryContact
      ),
      marketingCommunication:
        !!applicationMetadata?.marketingCommunicationSameAsCompanyPrimaryContact,
      hrEmployeeEngagement:
        !!applicationMetadata?.hrEmployeeEngagementSameAsCompanyPrimaryContact,
      sales: !!applicationMetadata?.salesSameAsCompanyPrimaryContact,
    },
    billingAddress: {
      streetAddress:
        extension?.accountsPayableBillingAddress?.streetAddress || "",
      city: extension?.accountsPayableBillingAddress?.city || "",
      provinceOrState:
        extension?.accountsPayableBillingAddress?.province || "",
      postalOrZipCode:
        extension?.accountsPayableBillingAddress?.postalCode || "",
      country: extension?.accountsPayableBillingAddress?.country || "Canada",
    },
    profile: {
      internalNotes: extension?.profile?.internalNotes || "",
      // Map to plain objects so Apollo's __typename never leaks into the form
      // state (and from there into the update mutation input).
      customFields: (extension?.profile?.customFields || []).map((field) => ({
        id: field.id,
        fieldName: field.fieldName,
        value: field.value,
      })),
    },
    logo: {
      fileName: member.logo || "",
      previewUrl: "",
    },
  };
};

const buildMemberInputFromForm = ({
  currentStatus,
  detail,
  formState,
}: {
  currentStatus: MemberStatus;
  detail: BackendMemberDetail;
  formState: UpdateMemberFormState;
}): ChamberMemberInput => {
  const member = detail.member;
  const extension = member.cmpExtension;
  const primaryVoting = formState.contacts.primaryVoting;
  const administrator = formState.contacts.administrator;
  const accountsPayable = formState.contacts.accountsPayable;
  const employeeAdoption = formState.contacts.employeeAdoption;
  const marketingCommunication = formState.contacts.marketingCommunication;
  const hrEmployeeEngagement = formState.contacts.hrEmployeeEngagement;
  const sales = formState.contacts.sales;
  // IMPORTANT: every field below is whitelisted from the form state (or copied
  // one scalar at a time from the loaded record), mirroring CreateMember's
  // buildMemberInput. Never spread Apollo query result objects into this
  // input: they carry __typename plus Return-only fields (source, stripe*,
  // pricingSnapshot, weightedEmployees, ...) that the GraphQL input types
  // reject, which made the update mutation fail.
  return {
    id: member.id,
    chamberId: member.chamberId,
    categoryId: formState.directory.directoryCategoryIds,
    name: formState.directory.companyName.trim(),
    description: toOptionalString(formState.directory.companyDescription),
    website: toOptionalString(formState.directory.companyWebsite),
    facebook: toOptionalString(formState.socialLinks.facebookUrl),
    instagram: toOptionalString(formState.socialLinks.instagramUrl),
    threads: toOptionalString(formState.socialLinks.threadsUrl),
    linkedin: toOptionalString(formState.socialLinks.linkedinUrl),
    twitter: toOptionalString(formState.socialLinks.xTwitterUrl),
    tiktok: toOptionalString(formState.socialLinks.tikTokUrl),
    youtube: toOptionalString(formState.socialLinks.youtubeUrl),
    googleReviewLink: toOptionalString(formState.directory.googleReviewUrl),
    address: {
      streetAddress: formState.address.streetAddress.trim(),
      city: formState.address.city.trim(),
      province: formState.address.provinceOrState.trim(),
      postalCode: formState.address.postalOrZipCode.trim(),
    },
    hideAddressFromDirectory:
      formState.address.hideStreetAddressInDirectories,
    phoneNumber: formState.directory.mainCompanyPhone.trim(),
    phoneNumberExtension: toOptionalString(
      formState.directory.mainCompanyPhoneExt
    ),
    contactName: toOptionalString(
      formState.directory.mainCompanyContactName
    ),
    email: formState.directory.mainCompanyContactEmail.trim(),
    logo: toOptionalString(getLogoInputValue(formState.logo)),
    showInApp: formState.directory.showInDirectory,
    adminConsoleLoginEmail: toOptionalString(administrator.email),
    adminConsoleContactName: toOptionalString(administrator.name),
    isSuspended: currentStatus === "Suspended" || currentStatus === "Past",
    cpaAdminCompanyAdminContactTitle: toOptionalString(administrator.title),
    cpaAdminCompanyAdminContactNumber: toOptionalString(
      administrator.companyPhone
    ),
    cpaAdminCompanyAdminContactNumberExtension: toOptionalString(
      administrator.ext
    ),
    primaryContactName: toOptionalString(primaryVoting.name),
    primaryContactTitle: toOptionalString(primaryVoting.title),
    primaryContactEmail: toOptionalString(primaryVoting.email),
    primaryContactPhoneNumber: toOptionalString(primaryVoting.companyPhone),
    primaryContactPhoneNumberExtension: toOptionalString(primaryVoting.ext),
    cpaEmployeeAdoptionContactName: toOptionalString(employeeAdoption.name),
    cpaEmployeeAdoptionContactEmail: toOptionalString(employeeAdoption.email),
    cpaEmployeeAdoptionContactTitle: toOptionalString(employeeAdoption.title),
    cpaEmployeeAdoptionContactNumber: toOptionalString(
      employeeAdoption.companyPhone
    ),
    cpaEmployeeAdoptionContactNumberExtension: toOptionalString(
      employeeAdoption.ext
    ),
    lifecycleStatus: lifecycleStatusByLabel[currentStatus],
    cmpExtension: {
      // Workflow tracking is copied scalar-by-scalar from the loaded record so
      // an update does not wipe approval / payment-request / archive state.
      boardApprovalRequired: extension?.boardApprovalRequired,
      boardApprovedAtUtcMilli: extension?.boardApprovedAtUtcMilli,
      boardApprovedByUserId: extension?.boardApprovedByUserId,
      paymentRequestState: extension?.paymentRequestState,
      lastPaymentRequestSentAtUtcMilli:
        extension?.lastPaymentRequestSentAtUtcMilli,
      archivedAtUtcMilli: extension?.archivedAtUtcMilli,
      archiveReason: extension?.archiveReason,
      accountsPayableContactName: toOptionalString(accountsPayable.name),
      accountsPayableContactTitle: toOptionalString(accountsPayable.title),
      accountsPayableContactEmail: toOptionalString(accountsPayable.email),
      accountsPayableContactNumber: toOptionalString(
        accountsPayable.companyPhone
      ),
      accountsPayableContactNumberExtension: toOptionalString(
        accountsPayable.ext
      ),
      accountsPayableBillingAddress: {
        streetAddress: toOptionalString(
          formState.billingAddress.streetAddress
        ),
        city: toOptionalString(formState.billingAddress.city),
        province: toOptionalString(formState.billingAddress.provinceOrState),
        postalCode: toOptionalString(formState.billingAddress.postalOrZipCode),
        country: toOptionalString(formState.billingAddress.country),
      },
      marketingCommunicationContactName: toOptionalString(
        marketingCommunication.name
      ),
      marketingCommunicationContactTitle: toOptionalString(
        marketingCommunication.title
      ),
      marketingCommunicationContactEmail: toOptionalString(
        marketingCommunication.email
      ),
      marketingCommunicationContactNumber: toOptionalString(
        marketingCommunication.companyPhone
      ),
      marketingCommunicationContactNumberExtension: toOptionalString(
        marketingCommunication.ext
      ),
      hrEmployeeEngagementContactName: toOptionalString(
        hrEmployeeEngagement.name
      ),
      hrEmployeeEngagementContactTitle: toOptionalString(
        hrEmployeeEngagement.title
      ),
      hrEmployeeEngagementContactEmail: toOptionalString(
        hrEmployeeEngagement.email
      ),
      hrEmployeeEngagementContactNumber: toOptionalString(
        hrEmployeeEngagement.companyPhone
      ),
      hrEmployeeEngagementContactNumberExtension: toOptionalString(
        hrEmployeeEngagement.ext
      ),
      salesBusinessDevelopmentContactName: toOptionalString(sales.name),
      salesBusinessDevelopmentContactTitle: toOptionalString(sales.title),
      salesBusinessDevelopmentContactEmail: toOptionalString(sales.email),
      salesBusinessDevelopmentContactNumber: toOptionalString(
        sales.companyPhone
      ),
      salesBusinessDevelopmentContactNumberExtension: toOptionalString(
        sales.ext
      ),
      association: {
        isAssociateCompanyMember:
          formState.association.isAssociateCompanyMember,
        primaryMemberCompanyId:
          toOptionalString(formState.association.primaryMemberCompanyId),
      },
      membership: buildProfileUpdateMembershipInput({
        persistedMembership: extension?.membership,
        emailCommunicationConsentConfirmed:
          formState.membership.emailCommunicationConsentConfirmed,
      }),
      companyDetails: {
        yearEstablished: toOptionalString(
          formState.companyDetails.yearEstablished
        ),
        howDidYouHearAboutUs: formState.companyDetails.howDidYouHearAboutUs,
        howDidYouHearAboutUsOther: toOptionalString(
          formState.companyDetails.howDidYouHearAboutUsOther
        ),
        primaryReasonsForJoining:
          formState.companyDetails.primaryReasonsForJoining,
        primaryReasonForJoiningOther: toOptionalString(
          formState.companyDetails.primaryReasonForJoiningOther
        ),
        activeSocialPlatforms: formState.companyDetails.activeSocialPlatforms,
        boardOrCommitteeInterest: toOptionalString(
          formState.companyDetails.boardOrCommitteeInterest
        ),
      },
      applicationMetadata: {
        country: toOptionalString(formState.address.country),
        primaryVotingSameAsMainCompanyContact:
          formState.contactSameAs.primaryVoting,
        administratorSameAsCompanyPrimaryContact:
          formState.contactSameAs.administrator,
        accountsPayableSameAsCompanyPrimaryContact:
          formState.contactSameAs.accountsPayable,
        employeeAdoptionSameAsCompanyPrimaryContact:
          formState.contactSameAs.employeeAdoption,
        marketingCommunicationSameAsCompanyPrimaryContact:
          formState.contactSameAs.marketingCommunication,
        hrEmployeeEngagementSameAsCompanyPrimaryContact:
          formState.contactSameAs.hrEmployeeEngagement,
        // Legacy aliases kept in sync exactly like CreateMember's builder.
        marketingSameAsCompanyPrimaryContact:
          formState.contactSameAs.administrator,
        hrSameAsCompanyPrimaryContact: formState.contactSameAs.employeeAdoption,
        salesSameAsCompanyPrimaryContact: formState.contactSameAs.sales,
        communicationPreferences:
          formState.insights.communicationPreferences,
      },
      profile: {
        internalNotes: toOptionalString(formState.profile.internalNotes),
        customFields: formState.profile.customFields
          .filter((field) => field.fieldName.trim())
          .map((field) => ({
            id: field.id,
            fieldName: field.fieldName.trim(),
            value: field.value,
          })),
      },
      chamberPerksAppHeardAbout: toOptionalString(
        formState.insights.chamberPerksAppHeardAbout
      ),
      chamberPerksAppDownloaded: toOptionalString(
        formState.insights.chamberPerksAppDownloaded
      ),
      boardDirectorInterest: toOptionalString(
        formState.insights.boardDirectorInterest
      ),
      committeeInterest: toOptionalString(
        formState.insights.committeeInterest
      ),
      referralTracking: formState.referralTracking
        .filter(
          (referral) =>
            referral.companyReferralName.trim() ||
            referral.contactName.trim() ||
            referral.email.trim() ||
            referral.contactNumber.trim()
        )
        .map((referral) => ({
          id: referral.id,
          referralDate: toOptionalString(referral.referralDate),
          becameMemberDate: toOptionalString(referral.becameMemberDate),
          companyReferralName: toOptionalString(referral.companyReferralName),
          contactName: toOptionalString(referral.contactName),
          email: toOptionalString(referral.email),
          contactNumber: toOptionalString(referral.contactNumber),
          contactNumberExtension: toOptionalString(
            referral.contactNumberExtension
          ),
        })),
      affiliatedChamberMember: {
        isAffiliatedChamberMember:
          formState.affiliatedChamberMember.isAffiliatedChamberMember,
        chamberName: toOptionalString(
          formState.affiliatedChamberMember.chamberName
        ),
      },
      affinityProgramEnrollments: formState.affinityProgramEnrollments.map(
        (enrollment) => ({
          id: enrollment.id,
          affinityProgramPartnerId: enrollment.affinityProgramPartnerId,
          affinityProgramPartnerName: enrollment.affinityProgramPartnerName,
          idNumber: toOptionalString(enrollment.idNumber),
          startDate: toOptionalString(enrollment.startDate),
          billingOption: toOptionalString(enrollment.billingOption),
          status: toOptionalString(enrollment.status),
          notes: toOptionalString(enrollment.notes),
        })
      ),
    },
  };
};

const getHeaderCopy = (status: MemberStatus) => {
  if (status === "Pending") {
    return {
      title: "Update Member",
      description:
        "Use this section to see member status, and update any member information.",
    };
  }

  if (status === "Active") {
    return {
      title: "Update Member",
      description:
        "Use this section to see member status, and update any member information. Also, send member login credentials reset password, suspend a member and delete member.",
    };
  }

  if (status === "Suspended") {
    return {
      title: "Update Member",
      description:
        "Use this section to see member status, and to unsuspend member.",
    };
  }

  return {
    title: "Update Member",
    description:
      "Use this section to see deleted member status, and to submit new company membership application.",
  };
};

const csvEscape = (value: string) => `"${value.replace(/"/g, '""')}"`;

// "Last Updated 12 days ago" / "May 28, 2026 at 3:20 PM" header meta per the
// Update Member mocks.
const lastUpdatedTimeFormatter = new Intl.DateTimeFormat("en-US", {
  hour: "numeric",
  minute: "2-digit",
  hour12: true,
});

const getLastUpdatedDaysAgoLabel = (utcMilli: number) => {
  const dayInMilli = 24 * 60 * 60 * 1000;
  const daysAgo = Math.max(0, Math.floor((Date.now() - utcMilli) / dayInMilli));

  if (daysAgo === 0) {
    return "Last Updated today";
  }

  return `Last Updated ${daysAgo} ${daysAgo === 1 ? "day" : "days"} ago`;
};

// The step-footer mocks label the last step "Membership Type and Billing"
// (spelled-out "and"), while the stepper shows "Membership Type & Billing".
const getFooterStepTitle = (stepIndex: number) => {
  const title = memberWorkflowSteps[stepIndex]?.title || "";
  return title === "Membership Type & Billing"
    ? "Membership Type and Billing"
    : title;
};

const StepFooter = ({
  activeStep,
  error,
  isReadOnly,
  loading,
  onBack,
  onNext,
  onSave,
}: {
  activeStep: number;
  error?: string;
  isReadOnly: boolean;
  loading: boolean;
  onBack: () => void;
  onNext: () => void;
  onSave: () => void;
}) => (
  <div className="mt-8 border-t border-gray-200 pt-5">
    {error && (
      <Alert severity="error" className="mb-4">
        {error}
      </Alert>
    )}
    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      {activeStep > 1 ? (
        <Button
          type="button"
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          disabled={loading}
          onClick={onBack}
          sx={{
            borderColor: "#D1D5DB",
            color: "#111827",
            fontWeight: 900,
            minWidth: 245,
            textAlign: "left",
            textTransform: "none",
          }}
        >
          <span className="leading-5">
            Previous:
            <br />
            {getFooterStepTitle(activeStep - 2)}
          </span>
        </Button>
      ) : (
        <div className="hidden sm:block" />
      )}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <Button
          type="button"
          variant="contained"
          disabled={isReadOnly || loading}
          onClick={onSave}
          sx={{
            backgroundColor: cpaGold,
            color: "white",
            fontWeight: 900,
            minWidth: 190,
            textTransform: "none",
            "&:hover": { backgroundColor: cpaGoldDark },
          }}
        >
          {loading ? "Updating..." : "Update Member"}
        </Button>
        {activeStep < 4 && (
          <Button
            type="button"
            variant="contained"
            onClick={onNext}
            disabled={loading}
            endIcon={<ArrowForwardIcon />}
            sx={{
              backgroundColor: cpaGold,
              color: "white",
              fontWeight: 900,
              minWidth: 270,
              textAlign: "left",
              textTransform: "none",
              "&:hover": { backgroundColor: cpaGoldDark },
            }}
          >
            <span className="leading-5">
              Next Step:
              <br />
              {getFooterStepTitle(activeStep)}
            </span>
          </Button>
        )}
      </div>
    </div>
  </div>
);

const StatusPill = ({
  tone,
  children,
}: {
  tone: "red" | "green" | "gray";
  children: React.ReactNode;
}) => (
  <span
    className={`inline-flex min-w-[120px] justify-center rounded-full px-4 py-1.5 text-xs font-bold ${
      tone === "green"
        ? "bg-[#16BE7E] text-white"
        : tone === "red"
          ? "bg-[#EF1B2D] text-white"
          : "bg-gray-100 text-gray-700"
    }`}
  >
    {children}
  </span>
);

// Member Status panel action buttons render as title-case pills per the
// update mocks (active/pending/past/suspended.png). Scoped to the status
// panel ONLY: popup/dialog buttons deliberately keep MUI's uppercase default
// (their mocks are all-caps), and the wizard footer/step buttons use the
// shared wizard constants (different font weights) — do not cross-apply.
const statusPanelButtonBaseSx = {
  fontWeight: 900,
  textTransform: "none",
  borderRadius: "9999px",
};

const statusPanelGoldButtonSx = {
  ...statusPanelButtonBaseSx,
  backgroundColor: cpaGold,
  "&:hover": { backgroundColor: cpaGoldDark },
};

const statusPanelGrayButtonSx = {
  ...statusPanelButtonBaseSx,
  backgroundColor: "#E5E7EB",
  color: "#111827",
  "&:hover": { backgroundColor: "#D1D5DB" },
};

const statusPanelRedButtonSx = {
  ...statusPanelButtonBaseSx,
  backgroundColor: "#EF1B2D",
  "&:hover": { backgroundColor: "#D71920" },
};

const statusPanelGreenButtonSx = {
  ...statusPanelButtonBaseSx,
  backgroundColor: "#00B83F",
  "&:hover": { backgroundColor: "#009934" },
};

const StatusChecklistRow = ({
  title,
  description,
  pill,
  action,
  icon,
  divider,
}: {
  title: string;
  description: string;
  pill?: React.ReactNode;
  action?: React.ReactNode;
  /** Row icon; defaults to the checked circle. The mock's "Active Member"
      stage shows an UNCHECKED circle until the member is active. */
  icon?: React.ReactNode;
  /** Opt-in bottom divider: the pending mock shows a single thin rule only
      under the approval-checkbox row; the other rows separate by whitespace. */
  divider?: boolean;
}) => (
  <div
    className={`flex gap-4 py-5${divider ? " border-b border-gray-200" : ""}`}
  >
    {icon || (
      <CheckCircleOutlineIcon
        sx={{ color: "#111827", fontSize: 26, mt: 0.25 }}
      />
    )}
    <div className="min-w-0">
      <p className="text-lg font-bold leading-6 text-black">{title}</p>
      <p className="mt-1 text-sm leading-6 text-black">{description}</p>
      {pill && <div className="mt-3">{pill}</div>}
      {action && <div className="mt-3">{action}</div>}
    </div>
  </div>
);

// Radio row control mirroring Create step 3 (Chamber Perks App questions).
const RadioChoiceGroup = ({
  label,
  options,
  value,
  onChange,
  columns = "compact",
  disabled,
  renderOptionLabel,
}: {
  label: string;
  options: string[];
  value: string;
  onChange: (value: string) => void;
  columns?: "compact" | "wide";
  disabled?: boolean;
  renderOptionLabel?: (option: string) => React.ReactNode;
}) => (
  <div className="mb-5">
    <p className="mb-2 text-sm font-bold text-gray-950">{label}</p>
    <RadioGroup
      row
      value={value}
      onChange={(event) => onChange(event.target.value)}
      className={
        columns === "wide"
          ? "grid grid-cols-1 gap-x-8 sm:grid-cols-3"
          : "grid grid-cols-1 gap-x-8 sm:grid-cols-2"
      }
    >
      {options.map((option) => (
        <FormControlLabel
          key={option}
          value={option}
          disabled={disabled}
          sx={choiceControlLabelSx}
          control={<Radio sx={radioControlSx} />}
          label={
            <span className="text-sm text-gray-950">
              {renderOptionLabel ? renderOptionLabel(option) : option}
            </span>
          }
        />
      ))}
    </RadioGroup>
  </div>
);

type InterestOptionMatrixRow = {
  id: string;
  label: string;
  affirmativeValue: string;
  value: string;
  onChange: (value: string) => void;
};

// Board-director/committee interest rows: affirmative sentence radio at the
// left plus No / Maybe radios to the right (step 3 screenshot spec).
const InterestOptionMatrix = ({
  label,
  rows,
  disabled,
}: {
  label: string;
  rows: InterestOptionMatrixRow[];
  disabled?: boolean;
}) => (
  <div className="mb-5">
    <p className="mb-2 text-sm font-bold text-gray-950">{label}</p>
    <div className="space-y-2">
      {rows.map((row) => (
        <RadioGroup
          key={row.id}
          row
          value={row.value}
          onChange={(event) => row.onChange(event.target.value)}
          className="grid grid-cols-1 gap-x-8 gap-y-2 sm:grid-cols-[minmax(0,1fr)_160px_160px]"
        >
          <FormControlLabel
            value={row.affirmativeValue}
            disabled={disabled}
            sx={choiceControlLabelSx}
            control={<Radio sx={radioControlSx} />}
            label={<span className="text-sm text-gray-950">{row.label}</span>}
          />
          {["No", "Maybe"].map((option) => (
            <FormControlLabel
              key={option}
              value={option}
              disabled={disabled}
              sx={choiceControlLabelSx}
              control={<Radio sx={radioControlSx} />}
              label={<span className="text-sm text-gray-950">{option}</span>}
            />
          ))}
        </RadioGroup>
      ))}
    </div>
  </div>
);

// Shared "status dot" rendering for affinity enrollments: used by the
// Enrolled Affinity Programs table and the Update modal's Status select so
// both surfaces stay in sync (mirrors Create step 3).
const AffinityEnrollmentStatusIndicator = ({ status }: { status: string }) => {
  const isInactive = status === "Inactive";

  return (
    <span
      className={`inline-flex items-center gap-2 font-semibold ${
        isInactive ? "text-red-600" : "text-green-700"
      }`}
    >
      <span
        className={`h-2 w-2 rounded-full ${
          isInactive ? "bg-red-500" : "bg-green-500"
        }`}
      />
      {status || "Active"}
    </span>
  );
};

const UpdateMember = ({
  onMembershipAdjustmentSubmit,
  onMembershipChangeInvoiceSend,
}: UpdateMemberProps = {}) => {
  const { memberId: legacyMemberId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const setPageHeaderOverride = usePageHeaderOverride();
  const chamber = useGetChamberFromUserIdInAuth();
  const chamberId = chamber?.id || "";
  const [memberIdForInput] = useState(
    () =>
      getUpdateMemberNavigationMemberId(location.state) ||
      consumeUpdateMemberNavigationMemberId()
  );
  const [activeStep, setActiveStep] = useState(1);
  const [formState, setFormState] = useState<UpdateMemberFormState>(() =>
    emptyFormState()
  );
  const [membershipAdjustmentEnabled, setMembershipAdjustmentEnabled] =
    useState(false);
  const membershipAdjustmentEnabledRef = useRef(false);
  const membershipAdjustmentOperationRef = useRef<{
    requestFingerprint: string;
    operationId: string;
  } | null>(null);
  const [membershipAdjustmentDraft, setMembershipAdjustmentDraft] =
    useState<MembershipFormState | null>(null);
  const [membershipAdjustmentError, setMembershipAdjustmentError] =
    useState("");
  const [membershipAdjustmentErrors, setMembershipAdjustmentErrors] =
    useState<MemberBillingErrors>({});
  const [membershipAdjustmentSubmitting, setMembershipAdjustmentSubmitting] =
    useState(false);
  const [membershipChangeInvoiceEmailSending, setMembershipChangeInvoiceEmailSending] =
    useState(false);
  const [currentStatus, setCurrentStatus] = useState<MemberStatus>("Pending");
  const [isDirty, setIsDirty] = useState(false);
  const [statusMessage, setStatusMessage] = useState<StatusMessage | null>(
    null
  );
  const [footerError, setFooterError] = useState("");
  const [logoError, setLogoError] = useState("");
  const [confirmationModal, setConfirmationModal] =
    useState<ConfirmationModal>(null);
  const [successDialog, setSuccessDialog] = useState<{
    title: string;
    tone: "green" | "red";
    // Optional body copy + action button (e.g. the Credentials Downloaded
    // pop-up's DOWNLOAD retry button per the credentials popup mocks).
    body?: React.ReactNode;
    actionLabel?: string;
    onAction?: () => void;
    secondaryActionLabel?: string;
    onSecondaryAction?: () => void;
    actionLoading?: boolean;
    actionError?: string;
  } | null>(null);
  const [credentialModalMode, setCredentialModalMode] =
    useState<CredentialModalMode>(null);
  const [previewEmailMode, setPreviewEmailMode] =
    useState<CredentialModalMode>(null);
  const [affinityDraft, setAffinityDraft] = useState<
    Omit<AffinityProgramEnrollment, "id" | "affinityProgramPartnerName">
  >({
    affinityProgramPartnerId: "",
    idNumber: "",
    startDate: "",
    billingOption: "",
    status: "Active",
    notes: "",
  });
  const [affinityEnrollmentError, setAffinityEnrollmentError] = useState("");
  // Save on the Affinity card first asks for confirmation (Affinity Program
  // Partner PROCEED/CANCEL modal per the step 3 spec annotations).
  const [affinityConfirmOpen, setAffinityConfirmOpen] = useState(false);
  // Row being edited via the Enrolled Affinity Programs table's Edit button
  // (the "Update {program name}" modal).
  const [editingAffinityEnrollment, setEditingAffinityEnrollment] =
    useState<AffinityProgramEnrollment | null>(null);

  const {
    data: detailData,
    loading: detailLoading,
    error: detailError,
    refetch: refetchDetail,
  } = useGetCmpMemberDetailQuery({
    variables: {
      input: {
        chamberId,
        memberId: memberIdForInput,
      },
    },
    skip: !chamberId || !memberIdForInput,
    fetchPolicy: "cache-and-network",
  });
  const { data: categoriesData } = useGetAllCategoriesQuery();
  const { data: chamberSettingsData, loading: chamberSettingsLoading } =
    useGetChamberMemberProSettingsQuery({
      variables: { chamberId },
      skip: !chamberId,
    });
  const {
    data: affinityProgramsData,
    loading: affinityProgramsLoading,
    error: affinityProgramsError,
  } = useGetAllAffinityProgramPartnersForChamberQuery({
    variables: { chamberId },
    skip: !chamberId,
    fetchPolicy: "cache-and-network",
  });
  // Primary Member companies for the Associate Member Company dropdown come
  // from the chamber's real (Active) member list, per the step 3 spec's
  // "Select the Primary Member Company" drop-down.
  const { data: primaryCompaniesData, loading: primaryCompaniesLoading } =
    useGetCmpManageMembersQuery({
      variables: {
        input: {
          chamberId,
          lifecycleStatuses: [CmpMemberLifecycleStatusEnum.Active],
        },
      },
      skip: !chamberId,
      fetchPolicy: "cache-and-network",
    });
  const { data: previewEmailData, loading: previewEmailLoading } =
    useGetSampleLoginInfoEmailQuery({
      variables: {
        chamberMemberId: memberIdForInput,
        type:
          previewEmailMode === "resetPassword"
            ? LoginInfoEmailTypes.ResetPassword
            : LoginInfoEmailTypes.BrandNew,
      },
      skip: !previewEmailMode || !memberIdForInput,
      fetchPolicy: "cache-and-network",
    });
  const [updateCmpMember, { loading: updatingMember }] =
    useUpdateCmpMemberMutation();
  const [changeCmpMemberMembership] = useChangeCmpMemberMembershipMutation();
  const [sendCmpMemberMembershipChangeInvoice] =
    useSendCmpMemberMembershipChangeInvoiceMutation();
  const [approveCmpMemberBoard, { loading: approvingBoard }] =
    useApproveCmpMemberBoardMutation();
  const [resendCmpMemberPaymentRequest, { loading: sendingInvoice }] =
    useResendCmpMemberPaymentRequestMutation();
  const [suspendCmpMember, { loading: suspendingMember }] =
    useSuspendCmpMemberMutation();
  const [activateCmpMember, { loading: activatingMember }] =
    useActivateCmpMemberMutation();
  const [archiveCmpMember, { loading: archivingMember }] =
    useArchiveCmpMemberMutation();
  const [startApplicationFromExisting, { loading: startingApplication }] =
    useStartCmpMemberApplicationFromExistingMutation();
  const [
    setNewTemporaryChamberMemberPassword,
    { loading: generatingCredentials },
  ] = useSetNewTemporaryChamberMemberPasswordMutation();
  const [sendLoginInfoEmailToChamberMember, { loading: sendingCredentials }] =
    useSendLoginInfoEmailToChamberMemberMutation();

  const detail = detailData?.getCmpMemberDetail;
  const member = detail?.member;
  const statusActions = detail?.statusActions;
  const isReadOnly = !!detail?.isReadOnly || currentStatus === "Past";
  const actionLoading =
    approvingBoard ||
    sendingInvoice ||
    suspendingMember ||
    activatingMember ||
    archivingMember ||
    startingApplication ||
    generatingCredentials ||
    sendingCredentials;
  const categories = categoriesData?.getAllCategories || [];
  const selectedCategories = categories.filter((category) =>
    formState.directory.directoryCategoryIds.includes(category.id)
  );
  const chamberSettings = chamberSettingsData?.getChamberMemberProSettings;
  const chamberGroupInsuranceCompanyName =
    chamberSettings?.paymentsAndTaxes.chamberGroupInsurancePayment.billingDetails
      .companyName;
  const membershipModels = useMemo(
    () =>
      chamberSettings?.membership.membershipModels.filter((model) => model.enabled) ||
      [],
    [chamberSettings?.membership.membershipModels]
  );
  const creditCardPaymentsReady = getCmpCreditCardPaymentsReady(
    chamberSettings?.paymentsAndTaxes.stripeConnect
  );
  const membershipBillingValue = membershipAdjustmentEnabled
    ? {
        ...(membershipAdjustmentDraft ||
          cloneMembershipFormState(formState.membership)),
        // Consent remains an ordinary member field and add-ons are immutable in
        // the adjustment flow, so both always come from persisted form state.
        emailCommunicationConsentConfirmed:
          formState.membership.emailCommunicationConsentConfirmed,
        selectedAdditionalCostIds: [
          ...formState.membership.selectedAdditionalCostIds,
        ],
      }
    : formState.membership;
  const membershipAdjustmentHasChanges = hasMembershipAdjustmentChanges({
    draft: membershipAdjustmentDraft,
    persisted: formState.membership,
  });
  const isPendingJohnsonGroupSelection =
    currentStatus === "Pending" &&
    membershipBillingValue.billingResponsibility === "chamberGroupInsurance";
  const isThirdPartyMembershipBilling =
    membershipBillingValue.billingResponsibility ===
      "affiliatedPrimaryCompany" ||
    membershipBillingValue.billingResponsibility === "chamberGroupInsurance";
  const monthlyScheduleRequiresUnavailableCard =
    !creditCardPaymentsReady && !isThirdPartyMembershipBilling;
  const enabledPaymentSchedules = (
    chamberSettings?.membership.enabledPaymentSchedules || [
      ChamberMemberProPaymentScheduleEnum.Annual,
    ]
  ).filter(
    (schedule) =>
      schedule !== ChamberMemberProPaymentScheduleEnum.Monthly ||
      !monthlyScheduleRequiresUnavailableCard
  );
  const hasUnavailableMonthlySchedule =
    monthlyScheduleRequiresUnavailableCard &&
    membershipBillingValue.paymentSchedule ===
      ChamberMemberProPaymentScheduleEnum.Monthly;
  const paymentScheduleOptions: MemberBillingOption[] =
    enabledPaymentSchedules.map((schedule) => ({
      value: schedule,
      label:
        schedule === ChamberMemberProPaymentScheduleEnum.Monthly
          ? "Monthly"
          : "Yearly",
    }));

  if (hasUnavailableMonthlySchedule) {
    paymentScheduleOptions.unshift({
      value: ChamberMemberProPaymentScheduleEnum.Monthly,
      label: "Monthly (Current - unavailable)",
      disabled: true,
    });
  }

  const shouldShowPaymentSchedule =
    enabledPaymentSchedules.length > 1 || hasUnavailableMonthlySchedule;
  const membershipDiscountEnabled =
    !!chamberSettings?.paymentsAndTaxes.membershipDiscount.enabled ||
    formState.membership.membershipDiscount.enabled;
  const allMembershipModels =
    chamberSettings?.membership.membershipModels || [];
  const selectedMembershipModel = allMembershipModels.find(
    (model) => model.id === membershipBillingValue.membershipTypeId
  );
  const previousMembershipModel = allMembershipModels.find(
    (model) => model.id === formState.membership.membershipTypeId
  );
  const persistedMembership = detail?.member.cmpExtension?.membership;
  const pendingMembershipChange =
    detail?.member.cmpExtension?.pendingMembershipChange;
  const membershipPresentation = getMemberMembershipPresentation({
    configuredOptions: membershipModels.map((model) => ({
      value: model.id,
      label: model.name,
    })),
    isSettingsLoading: chamberSettingsLoading,
    membershipTypeId: formState.membership.membershipTypeId,
    membershipTypeName:
      persistedMembership?.membershipTypeName || previousMembershipModel?.name,
  });
  const membershipOptions: MemberBillingOption[] =
    membershipPresentation.options;

  const membershipPaymentMethodOptions: MemberBillingOption[] = [];

  if (creditCardPaymentsReady) {
    membershipPaymentMethodOptions.push({
      value: "creditCard",
      label: "Credit Card",
    });
  }

  (chamberSettings?.paymentsAndTaxes.manualPaymentMethods || [])
    .filter((method) => method.enabled)
    .forEach((method) => {
      const value = getPaymentMethodFromChoice({
        type: CmpPaymentChoiceTypeEnum.Manual,
        manualPaymentMethodId: method.id,
      });

      if (
        value &&
        !membershipPaymentMethodOptions.some(
          (option) => option.value === value
        )
      ) {
        membershipPaymentMethodOptions.push({
          value,
          label: method.label,
        });
      }
    });

  const allowedMembershipPaymentMethodOptions =
    membershipBillingValue.paymentSchedule ===
    ChamberMemberProPaymentScheduleEnum.Monthly
      ? membershipPaymentMethodOptions.filter(
          (option) => option.value === "creditCard"
        )
      : membershipPaymentMethodOptions;

  if (
    membershipBillingValue.paymentMethod &&
    !allowedMembershipPaymentMethodOptions.some(
      (option) => option.value === membershipBillingValue.paymentMethod
    )
  ) {
    allowedMembershipPaymentMethodOptions.unshift({
      value: membershipBillingValue.paymentMethod,
      label: `${
        persistedMembership?.paymentChoice.manualPaymentMethodLabel ||
        "Current payment method"
      } (Current - unavailable)`,
      disabled: true,
    });
  }

  const displayedMembershipAddOns = useMemo(
    () => {
      type MembershipAddOnForDisplay = {
        id: string;
        code: string;
        name: string;
        description: string;
        annualAmountInDollars: number | null;
      };
      const addOnsById = new Map<string, MembershipAddOnForDisplay>();
      const configuredAddOns = (
        chamberSettings?.membership.membershipModels || []
      ).flatMap((model) => model.additionalCosts);

      // Match Chamber Set Up: show every enabled configured benefit, even
      // when this member did not select it. The entire card remains view-only.
      configuredAddOns
        .filter((cost) => cost.enabled)
        .forEach((cost) => {
          if (!addOnsById.has(cost.id)) {
            addOnsById.set(cost.id, cost);
          }
        });

      // A selected benefit can later be disabled or deleted from settings.
      // Keep those persisted selections visible for historical reference.
      formState.membership.selectedAdditionalCostIds.forEach(
        (additionalCostId) => {
          if (addOnsById.has(additionalCostId)) {
            return;
          }

          const configuredAddOn = configuredAddOns.find(
            (cost) => cost.id === additionalCostId
          );

          addOnsById.set(
            additionalCostId,
            configuredAddOn || {
              id: additionalCostId,
              code: additionalCostId,
              name: "Legacy add-on benefit",
              description:
                "This saved benefit is no longer available in Chamber Set Up. Its current amount is unavailable, it remains attached for historical reference, and it will not be charged by this membership update.",
              annualAmountInDollars: null,
            }
          );
        }
      );

      return Array.from(addOnsById.values());
    },
    [
      chamberSettings?.membership.membershipModels,
      formState.membership.selectedAdditionalCostIds,
    ]
  );
  const selectedAdditionalCostIds = new Set(
    formState.membership.selectedAdditionalCostIds
  );
  const selectedAddOnTotalInCents = displayedMembershipAddOns.reduce(
    (total, addOn) =>
      total +
      (selectedAdditionalCostIds.has(addOn.id)
        ? Math.round((addOn.annualAmountInDollars || 0) * 100)
        : 0),
    0
  );
  const selectedAddOnAmountUnavailable = displayedMembershipAddOns.some(
    (addOn) =>
      selectedAdditionalCostIds.has(addOn.id) &&
      addOn.annualAmountInDollars === null
  );
  const baseMembershipPrice = getMembershipBasePrice({
    fullTimeEmployees: membershipBillingValue.fullTimeEmployees,
    membershipModel: selectedMembershipModel,
    partTimeEmployees: membershipBillingValue.partTimeEmployees,
    partTimeEmployeesPerFullTime:
      chamberSettings?.membership.partTimeEmployeesPerFullTime,
    paymentSchedule: membershipBillingValue.paymentSchedule,
  });
  const persistedPricingSnapshot = persistedMembership?.pricingSnapshot;
  const isShowingPersistedUnavailableMembership =
    !!membershipBillingValue.membershipTypeId &&
    !selectedMembershipModel &&
    membershipBillingValue.membershipTypeId ===
      formState.membership.membershipTypeId;
  const baseMembershipAmountInCents = isShowingPersistedUnavailableMembership
    ? persistedPricingSnapshot?.baseAmountInCents || 0
    : baseMembershipPrice.amountInCents;
  // Updating an existing member uses regular renewal pricing. Signup
  // proration and its "month you are joining" language are Create-only.
  const effectiveMembershipAmountInCents = baseMembershipAmountInCents;
  const membershipDiscountInCents =
    membershipDiscountEnabled && membershipBillingValue.membershipDiscount.enabled
    ? Math.round(
        getNumber(membershipBillingValue.membershipDiscount.amount) * 100
      )
    : 0;
  const isMonthlySchedule =
    membershipBillingValue.paymentSchedule ===
      ChamberMemberProPaymentScheduleEnum.Monthly;
  const membershipInvoiceTaxRatePercentage =
    getMembershipInvoiceTaxRatePercentage({
      invoiceTaxSettings:
        chamberSettings?.paymentsAndTaxes.invoiceTaxSettings.membershipInvoices,
    });
  const getMembershipInvoiceTaxInCents = (taxableAmountInCents: number) =>
    Math.round((taxableAmountInCents * membershipInvoiceTaxRatePercentage) / 100);
  // BILL-002/003: a monthly membership is invoiced ONCE for the full
  // membership total (monthly price annualized over the 12-installment plan)
  // while add-on benefits go on a SECOND invoice at full (annual) amount.
  // Spec §8: step-4 totals are tax-inclusive to match Create Member's
  // presentation (display-only — saving still generates no invoices).
  const membershipInvoiceTaxableInCents = Math.max(
    0,
    (isMonthlySchedule
      ? baseMembershipAmountInCents * 12
      : effectiveMembershipAmountInCents) - membershipDiscountInCents
  );
  const membershipInvoiceTotalInCents =
    membershipInvoiceTaxableInCents +
    getMembershipInvoiceTaxInCents(membershipInvoiceTaxableInCents);
  const addOnInvoiceTotalInCents =
    selectedAddOnTotalInCents +
    getMembershipInvoiceTaxInCents(selectedAddOnTotalInCents);
  const invoiceTaxableInCents = Math.max(
    0,
    effectiveMembershipAmountInCents +
      selectedAddOnTotalInCents -
      membershipDiscountInCents
  );
  const invoiceTotalInCents =
    invoiceTaxableInCents + getMembershipInvoiceTaxInCents(invoiceTaxableInCents);
  const affinityProgramOptions =
    affinityProgramsData?.getAllAffinityProgramPartnersForChamber
      ?.filter(
        (program) =>
          program.activationStatus !==
          AffinityProgramPartnerActivationStatusEnum.Deactivated
      )
      .sort((firstProgram, secondProgram) =>
        firstProgram.companyName.localeCompare(secondProgram.companyName)
      ) || [];
  const primaryMemberCompanySelectOptions = (() => {
    const options = (primaryCompaniesData?.getCmpManageMembers.rows || [])
      .filter((row) => row.id !== (memberIdForInput || member?.id))
      .map((row) => ({ value: row.id, label: row.companyName }))
      .sort((firstOption, secondOption) =>
        firstOption.label.localeCompare(secondOption.label)
      );
    const storedPrimaryCompanyValue =
      formState.association.primaryMemberCompanyId;

    if (
      storedPrimaryCompanyValue &&
      !options.some((option) => option.value === storedPrimaryCompanyValue)
    ) {
      // Older records stored a typed-in company name instead of a member id;
      // keep the saved value selectable so it still renders.
      options.unshift({
        value: storedPrimaryCompanyValue,
        label: storedPrimaryCompanyValue,
      });
    }

    return options;
  })();
  const memberAdminEmail =
    formState.contacts.administrator.email ||
    member?.adminConsoleLoginEmail ||
    formState.directory.mainCompanyContactEmail;
  const memberAdminName =
    formState.contacts.administrator.name ||
    member?.adminConsoleContactName ||
    formState.directory.mainCompanyContactName;
  const redirectToManageMembers = useCallback((message: string) => {
    navigate(RoutePaths.chamberAdmin_members_manage, {
      replace: true,
      state: {
        statusMessage: {
          severity: "info",
          message,
        },
      },
    });
  }, [navigate]);

  const memberUpdatedAtUtcMilli = member?.updatedAtUtcMilli;

  useEffect(() => {
    const nextHeader = getHeaderCopy(currentStatus);

    setPageHeaderOverride({
      ...nextHeader,
      // Top-right "Last Updated" meta with clock icon per the Update mocks.
      actions: memberUpdatedAtUtcMilli ? (
        <div className="flex items-start gap-2 self-center text-black">
          <AccessTimeIcon sx={{ fontSize: 22, mt: 0.25 }} />
          <div>
            <p className="text-sm font-bold leading-5">
              {getLastUpdatedDaysAgoLabel(memberUpdatedAtUtcMilli)}
            </p>
            <p className="mt-0.5 text-sm leading-5">
              {cmpLongDateFormatter.format(
                new Date(memberUpdatedAtUtcMilli)
              )}{" "}
              at {lastUpdatedTimeFormatter.format(
                new Date(memberUpdatedAtUtcMilli)
              )}
            </p>
          </div>
        </div>
      ) : undefined,
    });

    return () => setPageHeaderOverride(null);
  }, [currentStatus, memberUpdatedAtUtcMilli, setPageHeaderOverride]);

  useEffect(() => {
    if (legacyMemberId) {
      redirectToManageMembers("Open members from Manage Members to update them.");
      return;
    }

    if (!memberIdForInput) {
      redirectToManageMembers("Select a member from Manage Members to update it.");
    }
  }, [legacyMemberId, memberIdForInput, redirectToManageMembers]);

  useEffect(() => {
    if (!detail || isDirty || membershipAdjustmentEnabledRef.current) {
      return;
    }

    setFormState(mapDetailToFormState(detail));
    setMembershipAdjustmentDraft(null);
    setMembershipAdjustmentError("");
    setMembershipAdjustmentErrors({});
    setCurrentStatus(statusLabels[detail.lifecycleStatus]);
  }, [detail, isDirty]);

  const markDirty = () => {
    setIsDirty(true);
    setStatusMessage(null);
    setFooterError("");
  };

  const scrollToTop = () => {
    const scrollContainer = document.querySelector(".flex-1.overflow-auto");

    if (scrollContainer) {
      scrollContainer.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const goToStep = (step: number) => {
    setActiveStep(step);
    setFooterError("");
    window.setTimeout(scrollToTop, 0);
  };

  const updateSection = <SectionName extends keyof UpdateMemberFormState>(
    sectionName: SectionName,
    value: Partial<UpdateMemberFormState[SectionName]>
  ) => {
    markDirty();
    setFormState((current) => ({
      ...current,
      [sectionName]: {
        ...current[sectionName],
        ...value,
      },
    }));
  };

  const handleMembershipAdjustmentEnabledChange = (enabled: boolean) => {
    if (enabled && pendingMembershipChange) {
      setMembershipAdjustmentError(
        "A membership change is already scheduled. Review the scheduled change before submitting another."
      );
      return;
    }

    membershipAdjustmentEnabledRef.current = enabled;
    membershipAdjustmentOperationRef.current = null;
    setMembershipAdjustmentEnabled(enabled);
    setMembershipAdjustmentError("");
    setMembershipAdjustmentErrors({});
    setStatusMessage(null);

    if (enabled) {
      setMembershipAdjustmentDraft(
        cloneMembershipFormState(formState.membership)
      );
      return;
    }

    // The persisted form-state membership is never mutated by gated controls,
    // so dropping the draft restores every locked field in one operation.
    setMembershipAdjustmentDraft(null);
  };

  const updateMembershipAdjustmentDraft = (
    value: Partial<MembershipFormState>
  ) => {
    setMembershipAdjustmentError("");
    setMembershipAdjustmentErrors({});
    setStatusMessage(null);
    setMembershipAdjustmentDraft((currentDraft) => ({
      ...(currentDraft || cloneMembershipFormState(formState.membership)),
      ...value,
      // Add-ons are immutable during a membership adjustment.
      selectedAdditionalCostIds: [
        ...formState.membership.selectedAdditionalCostIds,
      ],
    }));
  };

  const updateContact = (
    role: ContactRole,
    field: keyof MemberContactInfo,
    value: string
  ) => {
    markDirty();
    setFormState((current) => ({
      ...current,
      contacts: {
        ...current.contacts,
        [role]: {
          ...current.contacts[role],
          [field]: value,
        },
      },
    }));
  };

  // Toggles a contact card's "same as" checkbox (MEM-UPDATE-005). Checking
  // copies the source contact in (the administrator card copies the primary
  // voting contact, every other card copies the main company contact);
  // unchecking keeps the copied values so the chamber can tweak them, matching
  // CreateMember's behavior. The flag itself persists via applicationMetadata.
  const handleContactSameAsToggle = (role: ContactRole, checked: boolean) => {
    markDirty();
    setFormState((current) => {
      const sourceContact =
        role === "administrator"
          ? {
              name: current.contacts.primaryVoting.name,
              email: current.contacts.primaryVoting.email,
              companyPhone: current.contacts.primaryVoting.companyPhone,
              ext: current.contacts.primaryVoting.ext,
            }
          : {
              name: current.directory.mainCompanyContactName,
              email: current.directory.mainCompanyContactEmail,
              companyPhone: current.directory.mainCompanyPhone,
              ext: current.directory.mainCompanyPhoneExt,
            };

      return {
        ...current,
        contactSameAs: {
          ...current.contactSameAs,
          [role]: checked,
        },
        contacts: checked
          ? {
              ...current.contacts,
              [role]: {
                ...current.contacts[role],
                ...sourceContact,
              },
            }
          : current.contacts,
      };
    });
  };

  const updateReferral = (
    referralId: string,
    field: keyof ReferralTrackingInfo,
    value: string
  ) => {
    markDirty();
    setFormState((current) => ({
      ...current,
      referralTracking: current.referralTracking.map((referral) =>
        referral.id === referralId ? { ...referral, [field]: value } : referral
      ),
    }));
  };

  const addReferralTracking = () => {
    markDirty();
    setFormState((current) => ({
      ...current,
      referralTracking: [
        ...current.referralTracking,
        createEmptyReferralTracking(),
      ],
    }));
  };

  // Save on the Affinity Program Partner card first opens the confirmation
  // modal (PROCEED/CANCEL) per the step 3 spec annotation.
  const handleStartAffinityEnrollmentSave = () => {
    const program = affinityProgramOptions.find(
      (option) => option.id === affinityDraft.affinityProgramPartnerId
    );

    if (!program) {
      setAffinityEnrollmentError("Select an Affinity Program Partner.");
      return;
    }

    setAffinityEnrollmentError("");
    setAffinityConfirmOpen(true);
  };

  const handleConfirmAffinityEnrollment = () => {
    const program = affinityProgramOptions.find(
      (option) => option.id === affinityDraft.affinityProgramPartnerId
    );

    setAffinityConfirmOpen(false);

    if (!program) {
      return;
    }

    markDirty();
    setFormState((current) => ({
      ...current,
      affinityProgramEnrollments: [
        ...current.affinityProgramEnrollments,
        {
          id: createLocalId("affinity"),
          affinityProgramPartnerName: program.companyName,
          ...affinityDraft,
          status: affinityDraft.status || "Active",
        },
      ],
    }));
    setAffinityDraft({
      affinityProgramPartnerId: "",
      idNumber: "",
      startDate: "",
      billingOption: "",
      status: "Active",
      notes: "",
    });
  };

  const handleUpdateEditingAffinityEnrollment = (
    field: keyof AffinityProgramEnrollment,
    value: string
  ) => {
    setEditingAffinityEnrollment((current) =>
      current ? { ...current, [field]: value } : current
    );
  };

  const handleSaveEditingAffinityEnrollment = () => {
    if (!editingAffinityEnrollment) {
      return;
    }

    markDirty();
    setFormState((current) => ({
      ...current,
      affinityProgramEnrollments: current.affinityProgramEnrollments.map(
        (enrollment) =>
          enrollment.id === editingAffinityEnrollment.id
            ? editingAffinityEnrollment
            : enrollment
      ),
    }));
    setEditingAffinityEnrollment(null);
  };

  const handleLogoUpload = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    if (!file) {
      return;
    }

    if (!["image/png", "image/jpeg"].includes(file.type)) {
      setLogoError("Logo must be a PNG or JPG file.");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setLogoError("Logo must be 5MB or smaller.");
      return;
    }

    const reader = new FileReader();

    reader.onload = () => {
      setLogoError("");
      updateSection("logo", {
        fileName: file.name,
        previewUrl: reader.result as string,
      });
    };

    reader.readAsDataURL(file);
  };

  const handleLogoDelete = () => {
    setLogoError("");
    updateSection("logo", { fileName: "", previewUrl: "" });
  };

  const validateStep = (step: number) => {
    if (step === 1) {
      if (!formState.directory.companyName.trim()) {
        return "Member Name is required before continuing.";
      }

      if (formState.directory.directoryCategoryIds.length === 0) {
        return "Select 1 to 2 directory categories before continuing.";
      }
    }

    if (step === 2) {
      const requiredContacts = contactSections.flatMap((section) =>
        section.requiredFields.map((field) => ({
          role: section.role,
          field,
          title: section.title,
        }))
      );
      const missingContact = requiredContacts.find(
        ({ role, field }) => !formState.contacts[role][field].trim()
      );

      if (missingContact) {
        return `${missingContact.title} ${missingContact.field} is required before continuing.`;
      }

      const invalidEmail = Object.values(formState.contacts).find(
        (contact) => contact.email && !hasEmailShape(contact.email)
      );

      if (invalidEmail) {
        return "Review contact email addresses before continuing.";
      }
    }

    if (step === 3) {
      if (!isValidEstablishedYear(formState.companyDetails.yearEstablished)) {
        return "Enter a four-digit established year that is not in the future.";
      }

      if (formState.insights.communicationPreferences.length === 0) {
        return "Select at least one communication preference before continuing.";
      }
    }

    return "";
  };

  const handleNextStep = () => {
    const error = validateStep(activeStep);

    if (error) {
      setFooterError(error);
      return;
    }

    goToStep(activeStep + 1);
  };

  const applyActionResult = async (
    nextDetail: CmpMemberDetailFieldsFragment | null | undefined,
    successTitle: string
  ) => {
    if (nextDetail) {
      setFormState(mapDetailToFormState(nextDetail));
      setCurrentStatus(statusLabels[nextDetail.lifecycleStatus]);
      setIsDirty(false);
      membershipAdjustmentEnabledRef.current = false;
      membershipAdjustmentOperationRef.current = null;
      setMembershipAdjustmentEnabled(false);
      setMembershipAdjustmentDraft(null);
      setMembershipAdjustmentError("");
      setMembershipAdjustmentErrors({});
    }

    setSuccessDialog({ title: successTitle, tone: "green" });
    await refetchDetail();
  };

  const handleBoardApproval = async () => {
    if (!chamberId || !memberIdForInput) {
      return;
    }

    try {
      const result = await approveCmpMemberBoard({
        variables: {
          input: {
            chamberId,
            memberId: memberIdForInput,
          },
        },
      });

      await applyActionResult(
        result.data?.approveCmpMemberBoard.detail,
        "Member Approved Successfully!"
      );
    } catch (error) {
      setStatusMessage({
        severity: "error",
        message:
          error instanceof Error
            ? error.message
            : "Could not approve this member.",
      });
    }
  };

  // MEM-UPDATE-006: (re)send the membership invoice to the member while their
  // payment is outstanding. Pass an explicit invoiceId to (re)send a specific
  // invoice instead — e.g. the separate add-on benefits invoice (BILL-003).
  const handleSendInvoice = async (invoiceId?: string) => {
    if (!detail || !chamberId) {
      return;
    }

    try {
      const result = await resendCmpMemberPaymentRequest({
        variables: {
          input: {
            chamberId,
            // The navigation member id is consumed once from sessionStorage,
            // so fall back to the loaded member's id after a page refresh.
            memberId: memberIdForInput || detail.member.id,
            invoiceId: invoiceId || detail.currentInvoice?.id,
          },
        },
      });

      if (!result.data?.resendCmpMemberPaymentRequest.actionSuccessful) {
        throw new Error("The invoice email could not be sent.");
      }

      await applyActionResult(
        result.data.resendCmpMemberPaymentRequest.detail,
        "Invoice Sent Successfully!"
      );
    } catch (error) {
      setStatusMessage({
        severity: "error",
        message:
          error instanceof Error
            ? error.message
            : "Could not send the invoice to this member.",
      });
    }
  };

  const confirmAction = async () => {
    if (!confirmationModal || !chamberId || !memberIdForInput) {
      return;
    }

    try {
      if (confirmationModal === "resetPassword") {
        setConfirmationModal(null);
        setCredentialModalMode("resetPassword");
        return;
      }

      if (confirmationModal === "suspend") {
        const result = await suspendCmpMember({
          variables: {
            input: {
              chamberId,
              memberId: memberIdForInput,
            },
          },
        });

        await applyActionResult(
          result.data?.suspendCmpMember.detail,
          "Member Suspended Successfully!"
        );
      }

      if (confirmationModal === "activate") {
        const result = await activateCmpMember({
          variables: {
            input: {
              chamberId,
              memberId: memberIdForInput,
            },
          },
        });

        await applyActionResult(
          result.data?.activateCmpMember.detail,
          "Member Activated Successfully!"
        );
      }

      if (confirmationModal === "unsuspend") {
        const result = await activateCmpMember({
          variables: {
            input: {
              chamberId,
              memberId: memberIdForInput,
            },
          },
        });

        await applyActionResult(
          result.data?.activateCmpMember.detail,
          "Member Unsuspended Successfully!"
        );
      }

      if (confirmationModal === "delete") {
        const result = await archiveCmpMember({
          variables: {
            input: {
              chamberId,
              memberId: memberIdForInput,
              archivedAtUtcMilli: Date.now(),
              archiveReason: "Deleted from Update Member",
            },
          },
        });

        await applyActionResult(
          result.data?.archiveCmpMember.detail,
          "Member Deleted Successfully!"
        );
      }

      if (confirmationModal === "restartApplication") {
        const result = await startApplicationFromExisting({
          variables: {
            input: {
              chamberId,
              memberId: memberIdForInput,
              isCmpMemberModuleTestData: shouldMarkCmpModuleTestData,
            },
          },
        });
        const prefilledMember =
          result.data?.startCmpMemberApplicationFromExisting.member;

        if (!prefilledMember) {
          throw new Error("No prefilled member response was returned.");
        }

        navigate(RoutePaths.chamberAdmin_members_create, {
          state: {
            cmpPrefillMember: prefilledMember,
            cmpPrefillSourceMemberId: memberIdForInput,
          },
        });
      }

      setConfirmationModal(null);
    } catch (error) {
      setStatusMessage({
        severity: "error",
        message:
          error instanceof Error
            ? error.message
            : "Could not complete this member action.",
      });
    }
  };

  const applyMembershipAdjustmentResult = (
    nextDetail: CmpMemberDetailFieldsFragment
  ) => {
    const nextMembership = mapDetailToFormState(nextDetail).membership;

    setFormState((current) => ({
      ...current,
      membership: {
        ...nextMembership,
        // These fields are outside the dedicated membership-adjustment write.
        emailCommunicationConsentConfirmed:
          current.membership.emailCommunicationConsentConfirmed,
        selectedAdditionalCostIds: [
          ...current.membership.selectedAdditionalCostIds,
        ],
      },
    }));
    setCurrentStatus(statusLabels[nextDetail.lifecycleStatus]);
    membershipAdjustmentEnabledRef.current = false;
    membershipAdjustmentOperationRef.current = null;
    setMembershipAdjustmentEnabled(false);
    setMembershipAdjustmentDraft(null);
    setMembershipAdjustmentError("");
    setMembershipAdjustmentErrors({});
  };

  const handleSendMembershipChangeInvoice = async (
    input: UpdateMemberMembershipChangeInvoiceSendInput
  ) => {
    try {
      setMembershipChangeInvoiceEmailSending(true);
      setSuccessDialog((current) =>
        current
          ? { ...current, actionLoading: true, actionError: undefined }
          : current
      );
      const result = onMembershipChangeInvoiceSend
        ? await onMembershipChangeInvoiceSend(input)
        : (
            await sendCmpMemberMembershipChangeInvoice({
              variables: { input },
            })
          ).data?.sendCmpMemberMembershipChangeInvoice;

      if (!result?.actionCompletedSuccessfully) {
        throw new Error("The membership-change invoice email was not sent.");
      }

      setSuccessDialog(null);
      setStatusMessage({
        severity: "success",
        message:
          "The combined membership-change invoice email was sent successfully.",
      });
      void refetchDetail();
    } catch (error) {
      setSuccessDialog((current) =>
        current
          ? {
              ...current,
              actionLoading: false,
              onAction: () =>
                handleSendMembershipChangeInvoice(input),
              actionError:
                error instanceof Error
                  ? error.message
                  : "The membership-change invoice email could not be sent. The committed invoice change remains saved.",
            }
          : current
      );
    } finally {
      setMembershipChangeInvoiceEmailSending(false);
    }
  };

  const handleMembershipAdjustmentSubmit = async () => {
    if (!detail || !chamberId || !membershipAdjustmentDraft) {
      return;
    }

    const nextErrors: MemberBillingErrors = {};

    if (!membershipAdjustmentDraft.membershipTypeId) {
      nextErrors.membershipTypeId = "Select a membership type.";
    } else if (
      !membershipModels.some(
        (model) => model.id === membershipAdjustmentDraft.membershipTypeId
      )
    ) {
      nextErrors.membershipTypeId =
        "Select an available membership type before updating.";
    }

    if (
      selectedMembershipModel?.pricingModel ===
        ChamberMemberProMembershipPricingModelEnum.EmployeeRange &&
      !getNumber(membershipAdjustmentDraft.fullTimeEmployees) &&
      !getNumber(membershipAdjustmentDraft.partTimeEmployees)
    ) {
      nextErrors.employeeCount =
        "Enter at least one employee count for this membership type.";
    }

    if (
      membershipDiscountEnabled &&
      membershipAdjustmentDraft.membershipDiscount.enabled
    ) {
      if (!membershipAdjustmentDraft.membershipDiscount.code.trim()) {
        nextErrors.membershipDiscountCode = "Enter a discount code.";
      }

      if (!membershipAdjustmentDraft.membershipDiscount.description.trim()) {
        nextErrors.membershipDiscountDescription =
          "Enter a discount description.";
      }

      if (!getNumber(membershipAdjustmentDraft.membershipDiscount.amount)) {
        nextErrors.membershipDiscountAmount = "Enter a discount amount.";
      }
    }

    if (
      !chamberSettings?.paymentsAndTaxes.membershipDiscount.enabled &&
      membershipAdjustmentDraft.membershipDiscount.enabled
    ) {
      nextErrors.membershipDiscountAmount =
        "Clear the saved discount because membership discounts are disabled in Chamber Set Up.";
    }

    if (
      !chamberSettings?.membership.allowAutoRenewal &&
      membershipAdjustmentDraft.autoRenew
    ) {
      nextErrors.autoRenew =
        "Clear auto-renew because automatic renewal is disabled in Chamber Set Up.";
    }

    if (
      membershipAdjustmentDraft.billingResponsibility ===
        "affiliatedPrimaryCompany" &&
      !formState.association.primaryMemberCompanyId
    ) {
      nextErrors.billingResponsibility =
        "Select the primary company before invoicing the affiliated primary company.";
    }

    if (
      membershipAdjustmentDraft.billingResponsibility ===
        "chamberGroupInsurance" &&
      (!chamberSettings?.paymentsAndTaxes.chamberGroupInsurancePayment.enabled ||
        !chamberGroupInsuranceCompanyName)
    ) {
      nextErrors.billingResponsibility =
        "Configure chamber group insurance billing details before selecting this billing option.";
    }

    if (
      membershipAdjustmentDraft.billingResponsibility === "memberPays" &&
      !allowedMembershipPaymentMethodOptions.some(
        (option) =>
          !option.disabled &&
          option.value === membershipAdjustmentDraft.paymentMethod
      )
    ) {
      nextErrors.paymentMethod =
        "Select an available membership payment method.";
    }

    setMembershipAdjustmentErrors(nextErrors);

    if (Object.keys(nextErrors).length > 0) {
      setMembershipAdjustmentError(
        "Fix the highlighted membership details before updating."
      );
      return;
    }

    if (hasUnavailableMonthlySchedule) {
      setMembershipAdjustmentError(
        "Credit card payments are disabled for this chamber. Select Yearly before updating the membership type."
      );
      return;
    }

    if (
      !membershipAdjustmentHasChanges &&
      !isPendingJohnsonGroupSelection
    ) {
      setMembershipAdjustmentError(
        "Make at least one membership change before updating."
      );
      return;
    }

    const previousMembershipLabel =
      previousMembershipModel?.name ||
      detail.member.cmpExtension?.membership?.membershipTypeName ||
      "Current membership";
    const updatedMembershipLabel =
      selectedMembershipModel?.name || "Updated membership";
    const expectedMemberUpdatedAtUtcMilli =
      detail.member.updatedAtUtcMilli || detail.member.createdAtUtcMilli || 0;
    const expectedCurrentInvoiceId =
      detail.member.cmpExtension?.latestInvoiceId || undefined;
    const operationRequestFingerprint = JSON.stringify({
      membership: getMembershipAdjustmentFingerprint(
        membershipAdjustmentDraft
      ),
      expectedMemberUpdatedAtUtcMilli,
      expectedCurrentInvoiceId: expectedCurrentInvoiceId || null,
    });

    if (
      membershipAdjustmentOperationRef.current?.requestFingerprint !==
      operationRequestFingerprint
    ) {
      membershipAdjustmentOperationRef.current = {
        requestFingerprint: operationRequestFingerprint,
        operationId: createLocalId("membership-change"),
      };
    }

    const operationId = membershipAdjustmentOperationRef.current.operationId;
    const memberId = memberIdForInput || detail.member.id;
    const adjustmentInput: UpdateMemberMembershipAdjustmentInput = {
      chamberId,
      memberId,
      operationId,
      expectedMemberUpdatedAtUtcMilli,
      expectedCurrentInvoiceId,
      membership: {
        membershipTypeId: membershipAdjustmentDraft.membershipTypeId,
        fullTimeEmployees: membershipAdjustmentDraft.fullTimeEmployees,
        partTimeEmployees: membershipAdjustmentDraft.partTimeEmployees,
        paymentSchedule: membershipAdjustmentDraft.paymentSchedule,
        billingResponsibility:
          membershipAdjustmentDraft.billingResponsibility,
        paymentMethod: membershipAdjustmentDraft.paymentMethod,
        membershipDiscount: {
          ...membershipAdjustmentDraft.membershipDiscount,
        },
        autoRenew: membershipAdjustmentDraft.autoRenew,
      },
    };
    const isJohnsonGroupChange =
      adjustmentInput.membership.billingResponsibility ===
      "chamberGroupInsurance";

    try {
      setMembershipAdjustmentSubmitting(true);
      setMembershipAdjustmentError("");
      const result = onMembershipAdjustmentSubmit
        ? await onMembershipAdjustmentSubmit(adjustmentInput)
        : await (async () => {
            const membership = adjustmentInput.membership;
            const paymentResponsibility = getPaymentResponsibility(
              membership.billingResponsibility
            );
            const effectivePaymentMethod = getEffectiveUpdatePaymentMethod({
              creditCardPaymentsReady,
              manualPaymentMethods:
                chamberSettings?.paymentsAndTaxes.manualPaymentMethods || [],
              paymentMethod: membership.paymentMethod,
            });

            if (
              membership.billingResponsibility === "memberPays" &&
              !effectivePaymentMethod
            ) {
              throw new Error(
                "Select a valid member payment method before updating the membership."
              );
            }

            const paymentChoice = getPaymentChoiceType(
              membership.billingResponsibility,
              effectivePaymentMethod
            );
            const isThirdPartyPaid =
              paymentResponsibility ===
              ChamberMemberProMembershipModelPaymentResponsibilityEnum.ThirdPartyPays;
            const affiliatedPrimaryCompanyName =
              primaryMemberCompanySelectOptions.find(
                (option) =>
                  option.value === formState.association.primaryMemberCompanyId
              )?.label;
            const thirdPartyPayerName =
              membership.billingResponsibility === "chamberGroupInsurance"
                ? chamberGroupInsuranceCompanyName ||
                  chamberGroupInsurancePaymentMarker
                : affiliatedPrimaryCompanyName;
            const thirdPartyPaymentNotes =
              membership.billingResponsibility === "chamberGroupInsurance"
                ? chamberGroupInsurancePaymentMarker
                : membershipBillingOptions.find(
                    (option) =>
                      option.value === membership.billingResponsibility
                  )?.description;
            const mutationResult = await changeCmpMemberMembership({
              variables: {
                input: {
                  chamberId: adjustmentInput.chamberId,
                  memberId: adjustmentInput.memberId,
                  operationId: adjustmentInput.operationId,
                  expectedMemberUpdatedAtUtcMilli:
                    adjustmentInput.expectedMemberUpdatedAtUtcMilli,
                  expectedCurrentInvoiceId:
                    adjustmentInput.expectedCurrentInvoiceId,
                  membership: {
                    membershipTypeId: membership.membershipTypeId,
                    paymentSchedule:
                      membership.paymentSchedule as ChamberMemberProPaymentScheduleEnum,
                    fullTimeEmployees: getNumber(
                      membership.fullTimeEmployees
                    ),
                    partTimeEmployees: getNumber(
                      membership.partTimeEmployees
                    ),
                    membershipDiscount: {
                      enabled: membership.membershipDiscount.enabled,
                      code: toOptionalString(
                        membership.membershipDiscount.code
                      ),
                      description: toOptionalString(
                        membership.membershipDiscount.description
                      ),
                      amountInCents: Math.round(
                        getNumber(membership.membershipDiscount.amount) * 100
                      ),
                    },
                    paymentResponsibility,
                    thirdPartyPayerName: isThirdPartyPaid
                      ? toOptionalString(thirdPartyPayerName)
                      : null,
                    thirdPartyPaymentNotes: isThirdPartyPaid
                      ? toOptionalString(thirdPartyPaymentNotes)
                      : null,
                    paymentChoice: {
                      ...paymentChoice,
                      autoRenew: membership.autoRenew,
                      emailCommunicationConsentConfirmed:
                        formState.membership
                          .emailCommunicationConsentConfirmed,
                    },
                  },
                },
              },
            });
            const mutationPayload =
              mutationResult.data?.changeCmpMemberMembership;

            if (!mutationPayload) {
              throw new Error(
                "No membership change response was returned. Review the member before retrying."
              );
            }

            return mutationPayload;
          })();

      if (
        result.outcome ===
          CmpMemberMembershipChangeOutcomeEnum.RequiresManualReview ||
        !result.changeCompletedSuccessfully
      ) {
        const conflictingInvoices = result.conflictingInvoiceNumbers.length
          ? ` Conflicting invoices: ${result.conflictingInvoiceNumbers.join(
              ", "
            )}.`
          : "";

        setMembershipAdjustmentError(
          `${result.message}${conflictingInvoices} Refresh the member and review their invoices before trying again.`
        );
        setStatusMessage({ severity: "warning", message: result.message });
        return;
      }

      if (
        result.outcome === CmpMemberMembershipChangeOutcomeEnum.NoChange
      ) {
        applyMembershipAdjustmentResult(result.detail);
        setStatusMessage({ severity: "info", message: result.message });
        void refetchDetail();
        return;
      }

      if (
        isPendingJohnsonGroupSelection &&
        result.detail.lifecycleStatus !== CmpMemberLifecycleStatusEnum.Active
      ) {
        throw new Error(
          "Johnson Group billing was not activated. Refresh the member and review the current invoice before retrying."
        );
      }

      if (
        result.outcome ===
        CmpMemberMembershipChangeOutcomeEnum.MemberActivated
      ) {
        applyMembershipAdjustmentResult(result.detail);
        void refetchDetail();
        setSuccessDialog({
          title: "Member Activated Successfully!",
          tone: "green",
          body: (
            <div>
              <p>{result.message}</p>
              <p className="mt-4 text-sm font-bold leading-6 text-black">
                The existing Johnson Group invoice remains an internal chamber
                record and was not emailed to the member. No replacement invoice
                or payment request was created, and existing add-on benefits
                were not changed or recharged.
              </p>
            </div>
          ),
          secondaryActionLabel: "Close",
          onSecondaryAction: () => setSuccessDialog(null),
        });
        return;
      }

      const oldMembershipLabel =
        result.audit?.oldMembership.membershipTypeName ||
        previousMembershipLabel;
      const newMembershipLabel =
        result.audit?.newMembership.membershipTypeName ||
        updatedMembershipLabel;
      const oldRenewalAmountInCents =
        result.audit?.oldMembership.pricingSnapshot.renewalAmountInCents;
      const newRenewalAmountInCents =
        result.audit?.newMembership.pricingSnapshot.renewalAmountInCents;
      const hasRenewalAmounts =
        typeof oldRenewalAmountInCents === "number" &&
        typeof newRenewalAmountInCents === "number";
      const renewalDifferenceInCents = hasRenewalAmounts
        ? newRenewalAmountInCents - oldRenewalAmountInCents
        : null;
      const renewalImpactCopy =
        renewalDifferenceInCents === null
          ? "Renewal totals will follow the committed server-side membership result."
          : renewalDifferenceInCents === 0
            ? "The membership renewal amount will not change."
            : `The membership renewal amount will ${
                renewalDifferenceInCents > 0 ? "increase" : "decrease"
              } by ${formatCurrency(Math.abs(renewalDifferenceInCents))}.`;

      applyMembershipAdjustmentResult(result.detail);
      void refetchDetail();

      if (
        result.outcome ===
        CmpMemberMembershipChangeOutcomeEnum.QueuedForRenewal
      ) {
        setSuccessDialog({
          title: "Membership Type Change Scheduled",
          tone: "green",
          body: (
            <div>
              <p>{result.message}</p>
              <div className="mt-5 grid grid-cols-1 gap-4 rounded-lg border border-gray-200 bg-gray-50 p-4 text-left sm:grid-cols-2">
                <div>
                  <p className="text-xs font-bold uppercase tracking-wide text-gray-600">
                    Current Membership
                  </p>
                  <p className="mt-1 font-bold text-black">
                    {oldMembershipLabel}
                  </p>
                  {typeof oldRenewalAmountInCents === "number" && (
                    <p className="mt-1 text-lg font-bold text-black">
                      {formatCurrency(oldRenewalAmountInCents)} / renewal
                    </p>
                  )}
                </div>
                <div>
                  <p className="text-xs font-bold uppercase tracking-wide text-gray-600">
                    Scheduled Membership
                  </p>
                  <p className="mt-1 font-bold text-black">
                    {newMembershipLabel}
                  </p>
                  {typeof newRenewalAmountInCents === "number" && (
                    <p className="mt-1 text-lg font-bold text-black">
                      {formatCurrency(newRenewalAmountInCents)} / renewal
                    </p>
                  )}
                </div>
              </div>
              <p className="mt-4 text-sm font-bold leading-6 text-black">
                Effective at the existing renewal date: {formatUtcCalendarDate(
                  result.effectiveAtUtcMilli
                )}
              </p>
              <p className="mt-3 text-sm leading-6 text-black">
                {renewalImpactCopy}
              </p>
              <p className="mt-3 text-sm leading-6 text-black">
                The paid current-cycle invoice was not changed, and no new
                current-cycle invoice was generated. Renewal pricing will use
                the Chamber Set Up in effect when the renewal is generated.
              </p>
            </div>
          ),
          secondaryActionLabel: "Close",
          onSecondaryAction: () => setSuccessDialog(null),
        });
        return;
      }

      const replacedInvoiceNumber =
        result.replacedInvoice?.invoiceNumber || "-";
      const replacementInvoice = result.replacementInvoice;
      const replacementInvoiceNumber =
        replacementInvoice?.invoiceNumber || "-";
      const didReplaceInvoice =
        result.outcome ===
        CmpMemberMembershipChangeOutcomeEnum.InvoiceReplaced;
      const canSendInvoice =
        !isJohnsonGroupChange &&
        replacementInvoice?.invoiceStatus === CmpInvoiceStatusEnum.Unpaid &&
        replacementInvoice.amountDueInCents > 0;
      const isNoChargeInvoice =
        replacementInvoice?.invoiceStatus === CmpInvoiceStatusEnum.Waived ||
        replacementInvoice?.amountDueInCents === 0;
      const sendInput = canSendInvoice && replacementInvoice
        ? {
            chamberId,
            memberId,
            invoiceId: replacementInvoice.id,
            operationId: result.operationId,
          }
        : null;

      setSuccessDialog({
        title: isPendingJohnsonGroupSelection
          ? "Member Activated Successfully!"
          : "Membership Type Updated Successfully!",
        tone: "green",
        body: (
          <div>
            <p>{result.message}</p>
            <div className="mt-5 grid grid-cols-1 gap-4 rounded-lg border border-gray-200 bg-gray-50 p-4 text-left sm:grid-cols-2">
              <div>
                <p className="text-xs font-bold uppercase tracking-wide text-gray-600">
                  Previous
                </p>
                <p className="mt-1 font-bold text-black">
                  {oldMembershipLabel}
                </p>
                {typeof oldRenewalAmountInCents === "number" && (
                  <p className="mt-1 text-lg font-bold text-black">
                    {formatCurrency(oldRenewalAmountInCents)} / renewal
                  </p>
                )}
                {didReplaceInvoice && (
                  <p className="mt-1 text-sm text-black">
                    Invoice {replacedInvoiceNumber} canceled
                  </p>
                )}
              </div>
              <div>
                <p className="text-xs font-bold uppercase tracking-wide text-gray-600">
                  Updated
                </p>
                <p className="mt-1 font-bold text-black">
                  {newMembershipLabel}
                </p>
                {typeof newRenewalAmountInCents === "number" && (
                  <p className="mt-1 text-lg font-bold text-black">
                    {formatCurrency(newRenewalAmountInCents)} / renewal
                  </p>
                )}
                <p className="mt-1 text-sm text-black">
                  Invoice {replacementInvoiceNumber}
                </p>
                {typeof replacementInvoice?.totalAmountInCents === "number" && (
                  <p className="mt-1 text-lg font-bold text-black">
                    {formatCurrency(replacementInvoice.totalAmountInCents)}
                  </p>
                )}
              </div>
            </div>
            <p className="mt-4 text-sm leading-6 text-black">
              {renewalImpactCopy}
            </p>
            <p className="mt-5 text-base leading-7 text-black">
              {isJohnsonGroupChange
                ? "The replacement Johnson Group invoice was committed as an internal chamber record."
                : isNoChargeInvoice
                ? "The internal no-charge invoice was committed successfully."
                : "Would you like to send the committed invoice to this member?"}
            </p>
            <p className="mt-2 text-sm font-bold leading-6 text-black">
              {isPendingJohnsonGroupSelection
                ? "The member was automatically activated. The invoice was not sent to the member, and existing add-on benefits were not recharged."
                : isJohnsonGroupChange
                ? "The invoice was not sent to the member, and existing add-on benefits were not recharged."
                : isNoChargeInvoice
                ? "No payment request was sent. Existing add-on benefits were not recharged."
                : `${
                    didReplaceInvoice
                      ? "SEND INVOICE sends one combined cancellation and replacement-invoice email."
                      : "SEND INVOICE sends the newly created membership invoice email."
                  } Existing add-on benefits were not recharged. Canceling closes this prompt without undoing the saved membership and invoice change.`}
            </p>
          </div>
        ),
        ...(sendInput
          ? {
              actionLabel: "Send Invoice",
              onAction: () => handleSendMembershipChangeInvoice(sendInput),
            }
          : {}),
        secondaryActionLabel: sendInput ? "Cancel" : "Close",
        onSecondaryAction: () => setSuccessDialog(null),
      });
    } catch (error) {
      setMembershipAdjustmentError(
        error instanceof Error
          ? error.message
          : "The membership type could not be updated."
      );
    } finally {
      setMembershipAdjustmentSubmitting(false);
    }
  };

  const saveUpdate = async () => {
    if (!detail || !chamberId) {
      return;
    }

    if (membershipAdjustmentEnabled) {
      setActiveStep(4);
      setFooterError(
        "Finish the membership type adjustment or disable it before using Update Member."
      );
      window.setTimeout(scrollToTop, 0);
      return;
    }

    // The navigation member id is consumed once from sessionStorage, so it can
    // be empty after a page refresh; fall back to the id of the member the
    // detail query loaded instead of silently skipping the save.
    const memberIdForUpdate = memberIdForInput || detail.member.id;

    const error = validateStep(activeStep);

    if (error) {
      setFooterError(error);
      return;
    }

    try {
      const result = await updateCmpMember({
        variables: {
          input: {
            chamberId,
            memberId: memberIdForUpdate,
            data: buildMemberInputFromForm({
              currentStatus,
              detail,
              formState,
            }),
          },
        },
      });
      const nextDetail = result.data?.updateCmpMember.detail;

      if (!nextDetail) {
        throw new Error("No update response was returned.");
      }

      setFormState(mapDetailToFormState(nextDetail));
      setCurrentStatus(statusLabels[nextDetail.lifecycleStatus]);
      setIsDirty(false);
      membershipAdjustmentEnabledRef.current = false;
      membershipAdjustmentOperationRef.current = null;
      setMembershipAdjustmentEnabled(false);
      setMembershipAdjustmentDraft(null);
      setMembershipAdjustmentError("");
      setMembershipAdjustmentErrors({});
      setStatusMessage({
        severity: "success",
        message: "Member update saved.",
      });
      await refetchDetail();
      scrollToTop();
    } catch (error) {
      setStatusMessage({
        severity: "error",
        message:
          error instanceof Error
            ? error.message
            : "Could not save the member update.",
      });
    }
  };

  const downloadCredentials = async () => {
    if (!memberAdminEmail || !memberIdForInput) {
      setStatusMessage({
        severity: "error",
        message: "Administrator contact email is required for credentials.",
      });
      return;
    }

    try {
      const result = await setNewTemporaryChamberMemberPassword({
        variables: {
          adminConsoleLoginEmail: memberAdminEmail,
          chamberMemberId: memberIdForInput,
        },
      });
      const password =
        result.data?.setNewTemporaryChamberMemberPassword.password || "";

      if (!password) {
        throw new Error("No temporary password was returned.");
      }

      const rows = [
        ["Member Company", "Contact Name", "Email", "Temporary Password"],
        [
          formState.directory.companyName,
          memberAdminName,
          memberAdminEmail,
          password,
        ],
      ];
      const csv = rows.map((row) => row.map(csvEscape).join(",")).join("\n");
      const filename = `${formState.directory.companyName || "member"}-dashboard-credentials.csv`;

      downloadTextFile({
        filename,
        contentType: "text/csv;charset=utf-8",
        content: csv,
      });
      setSuccessDialog({
        title: "Credentials Downloaded",
        tone: "green",
        body: (
          <>
            Your member dashboard login credentials have been downloaded
            automatically. If the download didn’t start, click{" "}
            <strong>DOWNLOAD</strong> to try again.
          </>
        ),
        actionLabel: "Download",
        onAction: () =>
          downloadTextFile({
            filename,
            contentType: "text/csv;charset=utf-8",
            content: csv,
          }),
      });
      await refetchDetail();
    } catch (error) {
      setStatusMessage({
        severity: "error",
        message:
          error instanceof Error
            ? error.message
            : "Could not download credentials.",
      });
    }
  };

  const sendCredentialsEmail = async () => {
    if (!memberAdminEmail || !memberIdForInput) {
      setStatusMessage({
        severity: "error",
        message: "Administrator contact email is required for credentials.",
      });
      return;
    }

    try {
      const result = await sendLoginInfoEmailToChamberMember({
        variables: {
          adminConsoleLoginEmail: memberAdminEmail,
          chamberMemberId: memberIdForInput,
          type:
            credentialModalMode === "resetPassword"
              ? LoginInfoEmailTypes.ResetPassword
              : LoginInfoEmailTypes.BrandNew,
        },
      });

      if (!result.data?.sendLoginInfoEmailToChamberMember.loginInfoEmailSentSuccessfully) {
        throw new Error("The email service did not confirm delivery.");
      }

      setCredentialModalMode(null);
      setSuccessDialog({
        title:
          credentialModalMode === "resetPassword"
            ? "Member Dashboard temporary password has been auto emailed successfully."
            : "Member Dashboard Login Credential email successfully sent.",
        tone: "green",
      });
      await refetchDetail();
    } catch (error) {
      setStatusMessage({
        severity: "error",
        message:
          error instanceof Error
            ? error.message
            : "Could not send credentials email.",
      });
    }
  };

  const renderStatusPanel = () => {
    if (!detail || !member) {
      return null;
    }

    const paymentReceived = !!statusActions?.paymentReceived;
    const boardApproved =
      !statusActions?.boardApprovalRequired || !!statusActions?.boardApproved;
    // BILL-003: a monthly member with add-on benefits carries a SECOND,
    // separate full-amount invoice (invoiceKind AddOn — a new enum value that
    // lands with backend codegen, hence the defensive string comparison).
    // Surface its unpaid state separately from the membership invoice, whose
    // paid state can diverge from it.
    const unpaidAddOnInvoice = (detail.invoices || []).find(
      (invoice) =>
        String(invoice.invoiceKind || "")
          .replace(/[^a-zA-Z]/g, "")
          .toUpperCase() === "ADDON" &&
        String(invoice.invoiceStatus).toUpperCase() === "UNPAID" &&
        (invoice.amountDueInCents || 0) > 0
    );

    return (
      <section className="-mx-8 border-t border-gray-200 px-8 pb-8 pt-8">
        <div className="w-full">
          <div className="mb-8">
            <div>
              <h2 className="text-[28px] font-bold leading-9 text-black">
                Member Status
              </h2>
              {currentStatus === "Pending" && (
                <p className="mt-4 max-w-5xl text-base leading-7 text-black">
                  Member Status will be updated according to member payment.
                  Members requiring Chamber or Board approval will need to be
                  manually selected to have member status updated.
                </p>
              )}
            </div>
          </div>

          {currentStatus === "Pending" && (
            <div>
              {/* The pending mock's checklist pairs a "Pending ..." stage
                  (red chip) with its completed stage (green chip); each pair
                  renders as one row whose title/description/chip advance with
                  the member's real state. "and or" in the mock is corrected
                  to "and/or". */}
              <StatusChecklistRow
                divider
                title={
                  boardApproved ? "Member Approval" : "Pending Member Approval"
                }
                description="Application pending chamber and/or board approval."
                pill={
                  boardApproved ? (
                    <StatusPill tone="green">Approved</StatusPill>
                  ) : (
                    <StatusPill tone="red">Not Approved</StatusPill>
                  )
                }
                action={
                  statusActions?.canApproveBoard && !boardApproved ? (
                    <FormControlLabel
                      sx={choiceControlLabelSx}
                      control={
                        <Checkbox
                          disabled={actionLoading}
                          onChange={(event) => {
                            if (event.target.checked) {
                              handleBoardApproval();
                            }
                          }}
                          sx={checkboxControlSx}
                        />
                      }
                      label={
                        <span className="text-sm text-black">
                          Check box to provide chamber and/or board approval of
                          pending member.
                        </span>
                      }
                    />
                  ) : undefined
                }
              />
              <StatusChecklistRow
                title={paymentReceived ? "Member Payment" : "Pending Member Payment"}
                description={
                  paymentReceived
                    ? "Member payment has been received. (Automatically handled when payment is applied to invoice)"
                    : "Invoice has been sent; member payment has not been received."
                }
                pill={
                  paymentReceived ? (
                    <StatusPill tone="green">Paid</StatusPill>
                  ) : (
                    <StatusPill tone="red">Unpaid</StatusPill>
                  )
                }
                action={
                  statusActions?.canResendPaymentRequest && !paymentReceived ? (
                    <Button
                      variant="contained"
                      startIcon={<SendOutlinedIcon />}
                      disabled={actionLoading}
                      onClick={() => handleSendInvoice()}
                      sx={{ ...statusPanelGoldButtonSx, minWidth: 190 }}
                    >
                      {sendingInvoice ? "Sending Invoice..." : "Send Invoice"}
                    </Button>
                  ) : undefined
                }
              />
              {unpaidAddOnInvoice && (
                <StatusChecklistRow
                  title="Pending Add-on Benefits Payment"
                  description="Add-on benefits are invoiced separately at full amount and cannot be paid in installments. Member payment has not been received."
                  pill={<StatusPill tone="red">Unpaid</StatusPill>}
                  action={
                    statusActions?.canResendPaymentRequest ? (
                      <Button
                        variant="contained"
                        startIcon={<SendOutlinedIcon />}
                        disabled={actionLoading}
                        onClick={() => handleSendInvoice(unpaidAddOnInvoice.id)}
                        sx={{ ...statusPanelGoldButtonSx, minWidth: 190 }}
                      >
                        {sendingInvoice
                          ? "Sending Invoice..."
                          : "Send Invoice"}
                      </Button>
                    ) : undefined
                  }
                />
              )}
              <StatusChecklistRow
                title="Active Member"
                description={
                  statusActions?.canActivate
                    ? "Payment and any required approval are complete. Activate this member now."
                    : "Active member payment and if required approval has been received."
                }
                icon={
                  <RadioButtonUncheckedIcon
                    sx={{ color: "#111827", fontSize: 26, mt: 0.25 }}
                  />
                }
                action={
                  statusActions?.canActivate ? (
                    <Button
                      variant="contained"
                      disabled={actionLoading}
                      onClick={() => setConfirmationModal("activate")}
                      sx={{ ...statusPanelGreenButtonSx, minWidth: 190 }}
                    >
                      Activate Member
                    </Button>
                  ) : undefined
                }
              />
              <div className="mt-6 flex justify-start">
                <Button
                  variant="contained"
                  startIcon={<DeleteOutlineIcon />}
                  disabled={!statusActions?.canArchive || actionLoading}
                  onClick={() => setConfirmationModal("delete")}
                  sx={{ ...statusPanelRedButtonSx, minWidth: 190 }}
                >
                  Delete Member
                </Button>
              </div>
            </div>
          )}

          {currentStatus === "Active" && (
            <div>
              <div className="mb-6 flex gap-4">
                <CheckCircleOutlineIcon
                  sx={{ color: "#16BE7E", fontSize: 28, mt: 0.2 }}
                />
                <div>
                  <p className="text-lg font-bold leading-6 text-black">
                    Active Member
                  </p>
                  <p className="mt-1 text-sm leading-6 text-black">
                    Active member payment and if required approval has been
                    received.
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap items-start gap-4">
                <div>
                  <Button
                    variant="contained"
                    disabled={actionLoading}
                    onClick={() => setCredentialModalMode("sendCredentials")}
                    sx={{ ...statusPanelGoldButtonSx, minWidth: 190 }}
                  >
                    Send Credentials
                  </Button>
                  <p className="mt-2 text-xs font-bold text-black">
                    Last Sent: {formatTimestampDate(
                      member.credentialsSentAtUtcMilli
                    )}
                  </p>
                </div>
                {/* Green dot-bullet status text per the active mock; gray when
                    the member has not logged in yet (state not shown in the
                    mock). */}
                <div
                  className={`flex min-h-[42px] max-w-[180px] items-center gap-2 text-sm font-bold ${
                    member.hasSignedUp ? "text-green-700" : "text-gray-500"
                  }`}
                >
                  <span
                    className={`h-2.5 w-2.5 flex-shrink-0 rounded-full ${
                      member.hasSignedUp ? "bg-green-600" : "bg-gray-400"
                    }`}
                  />
                  {member.hasSignedUp
                    ? "Logged In To Member Dashboard"
                    : "Not Logged In To Member Dashboard"}
                </div>
                <Button
                  variant="contained"
                  startIcon={<LockResetIcon />}
                  disabled={!statusActions?.canResetPassword || actionLoading}
                  onClick={() => setConfirmationModal("resetPassword")}
                  sx={{ ...statusPanelGrayButtonSx, minWidth: 170 }}
                >
                  Reset Password
                </Button>
                <Button
                  variant="contained"
                  startIcon={<PauseCircleOutlineIcon />}
                  disabled={!statusActions?.canSuspend || actionLoading}
                  onClick={() => setConfirmationModal("suspend")}
                  sx={{ ...statusPanelGrayButtonSx, minWidth: 130 }}
                >
                  Suspend
                </Button>
                <Button
                  variant="contained"
                  startIcon={<DeleteOutlineIcon />}
                  disabled={!statusActions?.canArchive || actionLoading}
                  onClick={() => setConfirmationModal("delete")}
                  sx={{ ...statusPanelRedButtonSx, minWidth: 150 }}
                >
                  Delete Member
                </Button>
              </div>
            </div>
          )}

          {currentStatus === "Suspended" && (
            <div>
              <div className="flex gap-4">
                {/* Gold check icon per the suspended mock. The mock's status
                    title "Suspend Member" is corrected to "Suspended Member",
                    and its "Activate Member" button reference is corrected to
                    the actual "Unsuspend Member" button label. */}
                <CheckCircleOutlineIcon
                  sx={{ color: cpaGold, fontSize: 28, mt: 0.2 }}
                />
                <div>
                  <p className="text-lg font-bold leading-6 text-black">
                    Suspended Member
                  </p>
                  <p className="mt-2 max-w-5xl text-sm leading-6 text-black">
                    This member is currently suspended and is not able to access
                    member dashboard or chamber benefits. To reactivate member
                    click “<strong>Unsuspend Member</strong>” button or click “
                    <strong>Delete Member</strong>” button and the member
                    information will be stored as a <strong>Past Member</strong>
                    .
                  </p>
                </div>
              </div>
              <div className="mt-8 flex flex-wrap gap-4">
                <Button
                  variant="contained"
                  startIcon={<AutorenewIcon />}
                  disabled={!statusActions?.canActivate || actionLoading}
                  onClick={() => setConfirmationModal("unsuspend")}
                  sx={{ ...statusPanelGreenButtonSx, minWidth: 190 }}
                >
                  Unsuspend Member
                </Button>
                <Button
                  variant="contained"
                  startIcon={<DeleteOutlineIcon />}
                  disabled={!statusActions?.canArchive || actionLoading}
                  onClick={() => setConfirmationModal("delete")}
                  sx={{ ...statusPanelRedButtonSx, minWidth: 170 }}
                >
                  Delete Member
                </Button>
              </div>
            </div>
          )}

          {(currentStatus === "Past" || currentStatus === "Prospect") && (
            <div>
              <div className="flex gap-4">
                <CheckCircleOutlineIcon
                  sx={{ color: "#111827", fontSize: 28, mt: 0.2 }}
                />
                <div>
                  <p className="text-lg font-bold leading-6 text-black">
                    Deleted Member
                  </p>
                  <p className="mt-2 max-w-5xl text-sm leading-6 text-black">
                    This member is deleted and no longer has member login or
                    member benefits. All information is stored as{" "}
                    <strong>Past Member.</strong>
                  </p>
                </div>
              </div>
              <div className="mt-8">
                <Button
                  variant="contained"
                  disabled={!statusActions?.canStartNewApplication || actionLoading}
                  onClick={() => setConfirmationModal("restartApplication")}
                  sx={{ ...statusPanelGoldButtonSx, minWidth: 330 }}
                >
                  Submit New Company Membership Application
                </Button>
              </div>
            </div>
          )}
        </div>
      </section>
    );
  };

  const renderStep1 = () => (
    <div className="space-y-6">
      <SectionShell
        id="member-directory-information"
        title="Member Directory Information"
        description="Enter the member information as it will appear on both the website and the Chamber Perks App™ directories"
        variant="card"
      >
        <FormTextField
          id="member-name"
          label="Member Name"
          value={formState.directory.companyName}
          onChange={(value) =>
            updateSection("directory", { companyName: value })
          }
          placeholder="Enter Member Name as it will Appear in our directories"
          required
          disabled={isReadOnly}
        />
        <FormTextField
          id="member-description"
          label="Member Description"
          value={formState.directory.companyDescription}
          onChange={(value) =>
            updateSection("directory", { companyDescription: value })
          }
          placeholder="Enter Member Details About Your Company"
          multiline
          rows={5}
          maxLength={descriptionMaxLength}
          characterCountAlign="left"
          disabled={isReadOnly}
          description="Enter information about the member which will appear on their profile on both the website and the Chamber Perks App™ directories. Use our Intelligent Digital Assistant (IDA) to help generate the description. Simply enter content or website URL, then ask IDA to create a customized description."
        />
        <div className="grid grid-cols-1 gap-x-6 md:grid-cols-2">
          <FormTextField
            id="member-website"
            label="Member Website"
            value={formState.directory.companyWebsite}
            onChange={(value) =>
              updateSection("directory", { companyWebsite: value })
            }
            placeholder="Enter Member Website URL"
            disabled={isReadOnly}
          />
          <FormTextField
            id="member-google-review-url"
            label="Member Google Review URL"
            value={formState.directory.googleReviewUrl}
            onChange={(value) =>
              updateSection("directory", { googleReviewUrl: value })
            }
            placeholder="Enter Google Review URL"
            disabled={isReadOnly}
          />
        </div>
        <div className="grid grid-cols-1 gap-x-6 md:grid-cols-[1fr_1fr_0.75fr_0.3fr]">
          <FormTextField
            id="main-contact-name"
            label="Main Contact Name"
            value={formState.directory.mainCompanyContactName}
            onChange={(value) =>
              updateSection("directory", {
                mainCompanyContactName: value,
              })
            }
            placeholder="Enter Main Contact Name"
            disabled={isReadOnly}
          />
          <FormTextField
            id="main-contact-email"
            label="Main Contact Email"
            value={formState.directory.mainCompanyContactEmail}
            onChange={(value) =>
              updateSection("directory", {
                mainCompanyContactEmail: value,
              })
            }
            placeholder="Enter Main Contact Email"
            disabled={isReadOnly}
          />
          <FormTextField
            id="main-phone"
            label="Main Phone"
            value={formState.directory.mainCompanyPhone}
            onChange={(value) =>
              updateSection("directory", { mainCompanyPhone: value })
            }
            placeholder="Enter Main Phone"
            disabled={isReadOnly}
          />
          <FormTextField
            id="main-phone-ext"
            label="Ext."
            value={formState.directory.mainCompanyPhoneExt}
            onChange={(value) =>
              updateSection("directory", { mainCompanyPhoneExt: value })
            }
            placeholder="0000"
            disabled={isReadOnly}
          />
        </div>
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <LogoUploadField
            id="update-member-logo"
            fileName={formState.logo.fileName}
            previewUrl={formState.logo.previewUrl}
            error={logoError}
            disabled={isReadOnly}
            onUpload={handleLogoUpload}
            onRemove={handleLogoDelete}
          />
          <div>
            <label className="mb-2 block text-sm font-bold leading-5 text-gray-950">
              Website and Chamber Perks App™ Directory Categories{" "}
              <span className="text-red-500">*</span>
            </label>
            <p className="mb-3 text-sm leading-6 text-black">
              Select 1 to 2 categories from the dropdown that best represents
              your company. Going forward, our Intelligent Digital Assistant,
              IDA, will recommend the most relevant categories to ensure similar
              companies are grouped together for better visibility.
            </p>
            <Autocomplete
              multiple
              options={categories}
              disabled={isReadOnly}
              value={selectedCategories}
              getOptionLabel={(option) => option.name}
              onChange={(_, nextCategories) => {
                if (nextCategories.length > 2) {
                  setFooterError("Select no more than 2 directory categories.");
                  return;
                }

                updateSection("directory", {
                  directoryCategoryIds: nextCategories.map(
                    (category) => category.id
                  ) as ChamberMemberCategory[],
                });
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Select 1 to 2 categories"
                  sx={fieldSx}
                />
              )}
            />
          </div>
        </div>
      </SectionShell>

      <SectionShell id="member-address" title="Member Address" variant="card">
        <FormControlLabel
          sx={choiceControlLabelSx}
          control={
            <Checkbox
              checked={formState.address.hideStreetAddressInDirectories}
              disabled={isReadOnly}
              onChange={(event) =>
                updateSection("address", {
                  hideStreetAddressInDirectories: event.target.checked,
                })
              }
              sx={checkboxControlSx}
            />
          }
          label={
            <span className="text-sm text-black">
              Check here to hide Street Address in Member Directories
            </span>
          }
        />
        <div className="mt-5 grid grid-cols-1 gap-x-6 md:grid-cols-2">
          <FormTextField
            id="street-address"
            label="Street Address"
            value={formState.address.streetAddress}
            onChange={(value) =>
              updateSection("address", { streetAddress: value })
            }
            placeholder="Enter Street Address"
            disabled={isReadOnly}
          />
          <FormTextField
            id="city"
            label="City"
            value={formState.address.city}
            onChange={(value) => updateSection("address", { city: value })}
            placeholder="Enter City"
            disabled={isReadOnly}
          />
          <FormSelectField
            id="province"
            label="Province/State"
            value={formState.address.provinceOrState}
            onChange={(value) =>
              updateSection("address", { provinceOrState: value })
            }
            options={provinceOptions.map((province) => ({
              value: province,
              label: province,
            }))}
            placeholder="Select Province or State"
            disabled={isReadOnly}
          />
          <FormTextField
            id="postal-code"
            label="Postal/Zip Code"
            value={formState.address.postalOrZipCode}
            onChange={(value) =>
              updateSection("address", { postalOrZipCode: value })
            }
            placeholder="Enter Postal or Zip Code"
            disabled={isReadOnly}
          />
          <FormTextField
            id="country"
            label="Country"
            value={formState.address.country}
            onChange={(value) => updateSection("address", { country: value })}
            placeholder="Enter Country"
            disabled={isReadOnly}
          />
        </div>
      </SectionShell>

      <SectionShell
        id="links-to-social-media"
        title="Links to Social Media"
        variant="card"
      >
        <div className="grid grid-cols-1 gap-x-6 md:grid-cols-2">
          {[
            ["facebookUrl", "Facebook Business Page URL"],
            ["instagramUrl", "Instagram Account URL"],
            ["threadsUrl", "Threads Account URL"],
            ["linkedinUrl", "LinkedIn Page URL"],
            ["xTwitterUrl", "X (Twitter) Account URL"],
            ["tikTokUrl", "TikTok Account URL"],
            ["youtubeUrl", "Youtube Channel URL"],
          ].map(([field, label]) => (
            <FormTextField
              key={field}
              id={field}
              label={label}
              value={
                formState.socialLinks[field as keyof typeof formState.socialLinks]
              }
              onChange={(value) =>
                updateSection("socialLinks", {
                  [field]: value,
                } as Partial<UpdateMemberFormState["socialLinks"]>)
              }
              placeholder={`Enter ${label}`}
              disabled={isReadOnly}
            />
          ))}
        </div>
      </SectionShell>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-6">
      {contactSections.map((section) => {
        const contact = formState.contacts[section.role];

        return (
          <div
            key={section.role}
            className="rounded-lg border border-black bg-white p-6 md:p-8"
          >
            <h3 className="text-lg font-bold leading-6 text-black">
              {section.title}
            </h3>
            <p className="mt-5 text-sm leading-6 text-black">
              {section.description}
            </p>
            <p className="mt-6 text-sm font-bold leading-6 text-black">
              This contact information will NOT appear on our website and the
              Chamber Perks App™ directories, unless it is entered as the main
              company contact.
            </p>
            <FormControlLabel
              className="mt-6"
              sx={choiceControlLabelSx}
              control={
                <Checkbox
                  checked={formState.contactSameAs[section.role]}
                  disabled={isReadOnly}
                  onChange={(event) =>
                    handleContactSameAsToggle(
                      section.role,
                      event.target.checked
                    )
                  }
                  sx={checkboxControlSx}
                />
              }
              label={
                <span className="text-sm leading-6 text-black">
                  {section.sameAsLabel}
                </span>
              }
            />
            <div className="mt-7 grid grid-cols-1 gap-x-7 md:grid-cols-2">
              <FormTextField
                id={`${section.role}-name`}
                label={section.nameLabel}
                value={contact.name}
                onChange={(value) => updateContact(section.role, "name", value)}
                placeholder={`Enter ${section.nameLabel}`}
                required={section.requiredFields.includes("name")}
                disabled={isReadOnly}
              />
              <FormTextField
                id={`${section.role}-title`}
                label="Title"
                value={contact.title}
                onChange={(value) => updateContact(section.role, "title", value)}
                placeholder="Enter Contact Person's Title"
                disabled={isReadOnly}
              />
            </div>
            <div className="grid grid-cols-1 gap-x-7 md:grid-cols-2 xl:grid-cols-[1.1fr_0.85fr_0.3fr]">
              <FormTextField
                id={`${section.role}-email`}
                label="Contact Email"
                value={contact.email}
                onChange={(value) => updateContact(section.role, "email", value)}
                placeholder="Enter Contact Email"
                required={section.requiredFields.includes("email")}
                disabled={isReadOnly}
              />
              <FormTextField
                id={`${section.role}-phone`}
                label={section.phoneLabel}
                value={contact.companyPhone}
                onChange={(value) =>
                  updateContact(section.role, "companyPhone", value)
                }
                placeholder={`Enter ${section.phoneLabel}`}
                disabled={isReadOnly}
              />
              <FormTextField
                id={`${section.role}-ext`}
                label="Ext."
                value={contact.ext}
                onChange={(value) => updateContact(section.role, "ext", value)}
                placeholder="0000"
                disabled={isReadOnly}
              />
            </div>
            {section.role === "accountsPayable" && (
              <div className="mt-2 grid grid-cols-1 gap-x-7 md:grid-cols-2">
                <FormTextField
                  id="billing-street-address"
                  label="Billing Street Address"
                  value={formState.billingAddress.streetAddress}
                  onChange={(value) =>
                    updateSection("billingAddress", { streetAddress: value })
                  }
                  placeholder="Enter Billing Street Address"
                  disabled={isReadOnly}
                />
                <FormTextField
                  id="billing-city"
                  label="Billing City"
                  value={formState.billingAddress.city}
                  onChange={(value) =>
                    updateSection("billingAddress", { city: value })
                  }
                  placeholder="Enter Billing City"
                  disabled={isReadOnly}
                />
                <FormTextField
                  id="billing-province"
                  label="Billing Province/State"
                  value={formState.billingAddress.provinceOrState}
                  onChange={(value) =>
                    updateSection("billingAddress", {
                      provinceOrState: value,
                    })
                  }
                  placeholder="Enter Billing Province or State"
                  disabled={isReadOnly}
                />
                <FormTextField
                  id="billing-postal-code"
                  label="Billing Postal/Zip Code"
                  value={formState.billingAddress.postalOrZipCode}
                  onChange={(value) =>
                    updateSection("billingAddress", {
                      postalOrZipCode: value,
                    })
                  }
                  placeholder="Enter Billing Postal or Zip Code"
                  disabled={isReadOnly}
                />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-6">
      <SectionShell
        id="member-insights"
        title="Member Insights"
        description="Enter the details below to help your chamber build stronger relationships, improve member retention, identify interests, and measure member participation."
        variant="card"
      >
        <p className="mb-8 text-sm font-bold text-black">
          This section is for internal use only.
        </p>
        <FormTextField
          id="year-member-was-established"
          label="Year member was established"
          value={formState.companyDetails.yearEstablished}
          onChange={(value) =>
            updateSection("companyDetails", {
              yearEstablished: value.replace(/[^0-9]/g, "").slice(0, 4),
            })
          }
          placeholder="YYYY"
          helperText="Enter the four-digit year."
          inputMode="numeric"
          maxLength={4}
          disabled={isReadOnly}
        />
        <FormMultiSelectField
          id="how-did-member-hear-about-us"
          label="How did member hear about us?"
          selected={formState.companyDetails.howDidYouHearAboutUs}
          onChange={(selected) =>
            updateSection("companyDetails", {
              howDidYouHearAboutUs: selected,
            })
          }
          options={hearAboutOptions}
          placeholder="Select how you heard about us"
          disabled={isReadOnly}
        />
        {formState.companyDetails.howDidYouHearAboutUs.includes("Other") && (
          <FormTextField
            id="how-heard-other"
            label="Other Advertising Medium"
            value={formState.companyDetails.howDidYouHearAboutUsOther}
            onChange={(value) =>
              updateSection("companyDetails", {
                howDidYouHearAboutUsOther: value,
              })
            }
            placeholder="Enter other advertising medium"
            disabled={isReadOnly}
          />
        )}
        <FormMultiSelectField
          id="primary-reason-member-joined"
          label="Primary reason member joined"
          selected={formState.companyDetails.primaryReasonsForJoining}
          onChange={(selected) =>
            updateSection("companyDetails", {
              primaryReasonsForJoining: selected,
            })
          }
          options={reasonForJoiningOptions}
          placeholder="Select your reason for joining"
          disabled={isReadOnly}
        />
        {formState.companyDetails.primaryReasonsForJoining.includes(
          "Other"
        ) && (
          <FormTextField
            id="reason-other"
            label="Other Reason"
            value={formState.companyDetails.primaryReasonForJoiningOther}
            onChange={(value) =>
              updateSection("companyDetails", {
                primaryReasonForJoiningOther: value,
              })
            }
            placeholder="Enter other reason for joining"
            disabled={isReadOnly}
          />
        )}
        <MultiOptionGroup
          label="The social media platforms this member is active on?"
          selected={formState.companyDetails.activeSocialPlatforms}
          onChange={(selected) =>
            updateSection("companyDetails", {
              activeSocialPlatforms: selected.includes("None")
                ? ["None"]
                : selected,
            })
          }
          options={socialPlatformOptions}
          columns="inline"
          disabled={isReadOnly}
        />
        <RadioChoiceGroup
          label="Have you heard about the Chamber Perks App™?"
          options={yesNoOptions}
          value={formState.insights.chamberPerksAppHeardAbout}
          onChange={(value) =>
            updateSection("insights", { chamberPerksAppHeardAbout: value })
          }
          disabled={isReadOnly}
        />
        <RadioChoiceGroup
          label="Have you downloaded the Chamber Perks App™?"
          options={yesNoOptions}
          value={formState.insights.chamberPerksAppDownloaded}
          onChange={(value) =>
            updateSection("insights", { chamberPerksAppDownloaded: value })
          }
          disabled={isReadOnly}
        />
        <InterestOptionMatrix
          label="Select if member has shown interest in applying to become a chamber board director or join a committee"
          disabled={isReadOnly}
          rows={[
            {
              id: "board-director-interest",
              label: boardDirectorInterestAffirmative,
              affirmativeValue: boardDirectorInterestAffirmative,
              value: formState.insights.boardDirectorInterest,
              onChange: (value) =>
                updateSection("insights", { boardDirectorInterest: value }),
            },
            {
              id: "committee-interest",
              label: committeeInterestAffirmative,
              affirmativeValue: committeeInterestAffirmative,
              value: formState.insights.committeeInterest,
              onChange: (value) =>
                updateSection("insights", { committeeInterest: value }),
            },
          ]}
        />
        <MultiOptionGroup
          label="Select the method(s) this member wishes to receive communication from our chamber."
          selected={formState.insights.communicationPreferences}
          onChange={(selected) =>
            updateSection("insights", {
              communicationPreferences: selected,
            })
          }
          options={communicationPreferenceOptions}
          columns="single"
          required
          disabled={isReadOnly}
        />
      </SectionShell>

      <SectionShell
        id="member-referral-tracking"
        title="Member Referral Tracking"
        description="Track member referrals, monitor conversion rates, and recognize members who help grow your chamber community."
        variant="card"
      >
        <div className="space-y-6">
          {formState.referralTracking.map((referral) => (
            <div
              key={referral.id}
              className="grid grid-cols-1 gap-x-6 md:grid-cols-2"
            >
              <FormCalendarDateField
                id={`${referral.id}-referral-date`}
                label="Member Referral Date"
                value={referral.referralDate}
                onChange={(value) =>
                  updateReferral(referral.id, "referralDate", value)
                }
                placeholder="Enter Referral Date"
                iconPosition="right"
                disabled={isReadOnly}
              />
              <FormCalendarDateField
                id={`${referral.id}-became-member-date`}
                label="Became A Member Date (Will Automatically Populate If Member Joins)"
                value={referral.becameMemberDate}
                onChange={(value) =>
                  updateReferral(referral.id, "becameMemberDate", value)
                }
                placeholder="Date the Member Referral Joined"
                iconPosition="right"
                disabled={isReadOnly}
              />
              <FormTextField
                id={`${referral.id}-company-referral-name`}
                label="Company Referral Name"
                value={referral.companyReferralName}
                onChange={(value) =>
                  updateReferral(referral.id, "companyReferralName", value)
                }
                placeholder="Enter Company Referral Name"
                required
                disabled={isReadOnly}
              />
              <FormTextField
                id={`${referral.id}-contact-name`}
                label="Contact Name"
                value={referral.contactName}
                onChange={(value) =>
                  updateReferral(referral.id, "contactName", value)
                }
                placeholder="Enter Contact Name"
                disabled={isReadOnly}
              />
              <FormTextField
                id={`${referral.id}-email`}
                label="Email"
                value={referral.email}
                onChange={(value) => updateReferral(referral.id, "email", value)}
                placeholder="Enter Contact Email"
                disabled={isReadOnly}
              />
              <div className="grid grid-cols-[minmax(0,1fr)_140px] gap-x-4">
                <FormTextField
                  id={`${referral.id}-contact-number`}
                  label="Contact Number"
                  value={referral.contactNumber}
                  onChange={(value) =>
                    updateReferral(referral.id, "contactNumber", value)
                  }
                  placeholder="Enter Contact Number"
                  disabled={isReadOnly}
                />
                <FormTextField
                  id={`${referral.id}-extension`}
                  label="Ext."
                  value={referral.contactNumberExtension}
                  onChange={(value) =>
                    updateReferral(referral.id, "contactNumberExtension", value)
                  }
                  placeholder="0000"
                  disabled={isReadOnly}
                />
              </div>
            </div>
          ))}
        </div>
        <Button
          type="button"
          variant="contained"
          startIcon={<AddIcon />}
          onClick={addReferralTracking}
          disabled={isReadOnly}
          sx={wizardGoldButtonSx}
        >
          Add Another Referral
        </Button>
      </SectionShell>

      <SectionShell
        id="associate-member-company"
        title="Associate Member Company"
        description="Select if this member is an Associated Member Company and select the Primary Member Company from drop-down below."
        variant="card"
      >
        <FormControlLabel
          sx={{ alignItems: "flex-start", margin: 0 }}
          control={
            <Checkbox
              checked={formState.association.isAssociateCompanyMember}
              disabled={isReadOnly}
              onChange={(event) =>
                updateSection("association", {
                  isAssociateCompanyMember: event.target.checked,
                })
              }
              sx={checkboxControlSx}
            />
          }
          label={
            <span>
              <span className="block text-sm font-bold text-gray-950">
                Associate Member Company
              </span>
              <span className="mt-1 block max-w-5xl text-sm leading-6 text-black">
                Associate Members must be affiliated with an active Primary
                Member company. The Associate company must have a minimum of
                50% common ownership with the Primary Member company and must
                fall under the lower of the two membership fee categories.
              </span>
            </span>
          }
        />
        <div className="mt-8">
          <FormSelectField
            id="primary-member-company"
            label="Please select the affiliated Primary Member company below."
            value={formState.association.primaryMemberCompanyId}
            onChange={(value) =>
              updateSection("association", { primaryMemberCompanyId: value })
            }
            options={primaryMemberCompanySelectOptions}
            placeholder={
              primaryCompaniesLoading
                ? "Loading member companies"
                : "Select the Primary Member Company"
            }
            helperText={
              !primaryCompaniesLoading &&
              primaryMemberCompanySelectOptions.length === 0
                ? "No primary member companies available"
                : undefined
            }
            disabled={
              isReadOnly ||
              primaryCompaniesLoading ||
              !formState.association.isAssociateCompanyMember ||
              primaryMemberCompanySelectOptions.length === 0
            }
          />
        </div>
      </SectionShell>

      <SectionShell
        id="affiliated-chamber-member-company"
        title="Affiliated Chamber Member Company"
        description="Select if this member is affiliated with another Chamber and enter the chamber name in the box below."
        variant="card"
      >
        <FormControlLabel
          sx={{ alignItems: "flex-start", margin: 0 }}
          control={
            <Checkbox
              checked={
                formState.affiliatedChamberMember.isAffiliatedChamberMember
              }
              disabled={isReadOnly}
              onChange={(event) =>
                updateSection("affiliatedChamberMember", {
                  isAffiliatedChamberMember: event.target.checked,
                })
              }
              sx={checkboxControlSx}
            />
          }
          label={
            <span>
              <span className="block text-sm font-bold text-gray-950">
                Affiliated Chamber Member
              </span>
              <span className="mt-1 block max-w-5xl text-sm leading-6 text-black">
                This member is affiliated with the chamber below and therefore
                may be eligible for special membership pricing with your
                chamber if enabled.
              </span>
            </span>
          }
        />
        <div className="mt-8">
          <FormTextField
            id="affiliated-chamber-name"
            label="Enter the Affiliated Chamber of Commerce Name"
            value={formState.affiliatedChamberMember.chamberName}
            onChange={(value) =>
              updateSection("affiliatedChamberMember", { chamberName: value })
            }
            placeholder="Chamber of Commerce Name"
            disabled={
              isReadOnly ||
              !formState.affiliatedChamberMember.isAffiliatedChamberMember
            }
          />
        </div>
      </SectionShell>

      <SectionShell
        id="affinity-program-partner"
        title="Affinity Program Partner"
        description="Track the Affinity programs this member is enrolled in, including program identification numbers, enrollment dates, billing preferences, and participation status."
        variant="card"
      >
        {affinityProgramsError && (
          <Alert severity="warning" className="mb-5">
            Unable to load Affinity Program Partners from Chamber Set Up. Try
            refreshing before adding a program.
          </Alert>
        )}
        {!affinityProgramsLoading &&
          !affinityProgramsError &&
          affinityProgramOptions.length === 0 && (
            <Alert severity="info" className="mb-5">
              No Affinity Program Partners have been configured in Chamber Set
              Up yet.
            </Alert>
          )}
        <div className="grid grid-cols-1 gap-x-6 lg:grid-cols-4">
          <FormSelectField
            id="affinity-program"
            label="Affinity Program Partner"
            description="Select from the available programs"
            value={affinityDraft.affinityProgramPartnerId}
            onChange={(value) => {
              setAffinityEnrollmentError("");
              setAffinityDraft((current) => ({
                ...current,
                affinityProgramPartnerId: value,
              }));
            }}
            options={affinityProgramOptions.map((program) => ({
              value: program.id,
              label: program.companyName,
            }))}
            placeholder={
              affinityProgramsLoading
                ? "Loading affinity programs"
                : "Select an Affinity program"
            }
            error={affinityEnrollmentError}
            disabled={isReadOnly || affinityProgramsLoading}
          />
          <FormTextField
            id="affinity-id-number"
            label="ID Number"
            description="Enter the program identification number."
            value={affinityDraft.idNumber}
            onChange={(value) =>
              setAffinityDraft((current) => ({
                ...current,
                idNumber: value,
              }))
            }
            placeholder="Enter ID number"
            disabled={isReadOnly}
          />
          <FormCalendarDateField
            id="affinity-start-date"
            label="Start Date"
            description="Enter the date the program participation started."
            value={affinityDraft.startDate}
            onChange={(value) =>
              setAffinityDraft((current) => ({
                ...current,
                startDate: value,
              }))
            }
            placeholder="Select Date"
            iconPosition="right"
            disabled={isReadOnly}
          />
          <FormSelectField
            id="affinity-billing-option"
            label="Billing Option"
            description="How this program will be billed to members"
            value={affinityDraft.billingOption}
            onChange={(value) =>
              setAffinityDraft((current) => ({
                ...current,
                billingOption: value,
              }))
            }
            options={affinityBillingOptions.map((option) => ({
              value: option,
              label: renderAffinityBillingOptionLabel(option),
            }))}
            placeholder="Select billing option"
            disabled={isReadOnly}
          />
        </div>
        <FormTextField
          id="affinity-notes"
          label={
            <>
              Notes <span className="font-normal">(Optional)</span>
            </>
          }
          description="Additional information about this program."
          value={affinityDraft.notes}
          onChange={(value) =>
            setAffinityDraft((current) => ({
              ...current,
              notes: value,
            }))
          }
          placeholder="Enter any notes about this program"
          multiline
          rows={3}
          disabled={isReadOnly}
        />
        <div className="grid grid-cols-1 items-center gap-4 md:grid-cols-[minmax(0,1fr)_220px]">
          <p className="text-sm leading-6 text-black">
            Click <strong>Save</strong> to display details below in the
            Enrolled Affinity Programs table where you can update status,
            billing options and notes.
          </p>
          <Button
            type="button"
            variant="contained"
            disabled={
              isReadOnly ||
              affinityProgramsLoading ||
              affinityProgramOptions.length === 0
            }
            onClick={handleStartAffinityEnrollmentSave}
            sx={{
              backgroundColor: cpaGold,
              color: "white",
              fontWeight: 900,
              textTransform: "none",
              "&:hover": { backgroundColor: cpaGoldDark },
            }}
          >
            Save
          </Button>
        </div>
      </SectionShell>

      <SectionShell
        id="enrolled-affinity-programs"
        title="Enrolled Affinity Programs"
        description="Select edit to update and review all Affinity programs this member is currently enrolled in. Chambers should adjust start date to reflect actual start date for this member if previously enrolled."
        variant="card"
      >
        {formState.affinityProgramEnrollments.length === 0 ? (
          <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 px-5 py-6 text-sm text-black">
            No Affinity Programs have been enrolled for this member yet.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-[980px] w-full text-left text-sm text-black">
              <thead>
                <tr className="bg-gray-50 text-sm font-bold text-gray-950">
                  <th className="px-4 py-4">Affinity Program Partner</th>
                  <th className="px-4 py-4">ID Number</th>
                  <th className="px-4 py-4">Start Date</th>
                  <th className="px-4 py-4">Billing Options</th>
                  <th className="px-4 py-4">Status</th>
                  <th className="px-4 py-4">Notes</th>
                  <th className="px-4 py-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {formState.affinityProgramEnrollments.map((enrollment) => (
                  <tr
                    key={enrollment.id}
                    className="border-t border-gray-300 align-top"
                  >
                    <td className="px-4 py-5 font-semibold">
                      {enrollment.affinityProgramPartnerName}
                    </td>
                    <td className="px-4 py-5">{enrollment.idNumber || "-"}</td>
                    <td className="px-4 py-5">
                      {formatDateInputValueLong(enrollment.startDate)}
                    </td>
                    <td className="px-4 py-5">
                      {enrollment.billingOption
                        ? renderAffinityBillingOptionLabel(
                            enrollment.billingOption
                          )
                        : "-"}
                    </td>
                    <td className="px-4 py-5">
                      <AffinityEnrollmentStatusIndicator
                        status={enrollment.status}
                      />
                    </td>
                    <td className="px-4 py-5">{enrollment.notes || "-"}</td>
                    <td className="px-4 py-5 text-right">
                      <Button
                        type="button"
                        variant="contained"
                        size="small"
                        startIcon={<EditOutlinedIcon />}
                        disabled={isReadOnly}
                        onClick={() => setEditingAffinityEnrollment(enrollment)}
                        sx={wizardGoldButtonSx}
                      >
                        Edit
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SectionShell>
    </div>
  );

  const renderStep4 = () => (
    <MemberBillingStep
      value={membershipBillingValue}
      membershipOptions={membershipOptions}
      selectedMembershipSummary={{
        code:
          baseMembershipPrice.code !== "-"
            ? baseMembershipPrice.code
            : persistedPricingSnapshot?.pricingKey || "-",
        description:
          selectedMembershipModel?.description ||
          (isShowingPersistedUnavailableMembership
            ? "This saved membership type is no longer available in the current Chamber Set Up. It remains attached for reference; enable membership adjustment to replace it."
            : !membershipBillingValue.membershipTypeId
              ? "No membership is currently assigned to this member."
              : "Investment amount is automatically populated based on the membership selected."),
        amountLabel: formatCurrency(effectiveMembershipAmountInCents),
      }}
      paymentScheduleOptions={paymentScheduleOptions}
      paymentMethodOptions={allowedMembershipPaymentMethodOptions}
      paymentMethodTitle="Membership Payment Method"
      paymentMethodDescription="Select how this member's replacement or future renewal membership invoice will be paid. Available methods come from Chamber Set Up."
      billingOptions={membershipBillingOptions}
      addOns={displayedMembershipAddOns.map((addOn) => ({
        id: addOn.id,
        code: addOn.code,
        name: addOn.name,
        description: addOn.description,
        amountLabel:
          addOn.annualAmountInDollars === null
            ? "Amount unavailable"
            : formatCurrency(Math.round(addOn.annualAmountInDollars * 100)),
      }))}
      addOnsTotalLabel={
        selectedAddOnAmountUnavailable
          ? "Amount unavailable"
          : formatCurrency(selectedAddOnTotalInCents)
      }
      invoiceTotalLabel={formatCurrency(invoiceTotalInCents)}
      isMonthlySchedule={isMonthlySchedule}
      membershipInvoiceTotalLabel={formatCurrency(
        membershipInvoiceTotalInCents
      )}
      addOnInvoiceTotalLabel={formatCurrency(addOnInvoiceTotalInCents)}
      showPaymentSchedule={shouldShowPaymentSchedule}
      showPaymentMethod={
        membershipBillingValue.billingResponsibility === "memberPays"
      }
      paymentScheduleDescription={
        hasUnavailableMonthlySchedule
          ? "Credit card payments are disabled for this chamber. Select Yearly before saving membership billing changes."
          : undefined
      }
      monthlyPaymentsAvailable={creditCardPaymentsReady}
      membershipDiscountEnabled={membershipDiscountEnabled}
      membershipDiscountAvailable={
        !!chamberSettings?.paymentsAndTaxes.membershipDiscount.enabled
      }
      autoRenewAvailable={!!chamberSettings?.membership.allowAutoRenewal}
      membershipAdjustment={{
        enabled: membershipAdjustmentEnabled,
        hasChanges:
          membershipAdjustmentHasChanges || isPendingJohnsonGroupSelection,
        submitting: membershipAdjustmentSubmitting,
        error: membershipAdjustmentError,
        blockedReason: isReadOnly
          ? "This member is read-only and their membership billing cannot be changed."
          : pendingMembershipChange
            ? "A membership change is already scheduled. Review its status before submitting another change."
            : undefined,
        onEnabledChange: handleMembershipAdjustmentEnabledChange,
        onSubmit: handleMembershipAdjustmentSubmit,
      }}
      scheduledMembershipChange={
        pendingMembershipChange
          ? {
              statusLabel:
                pendingMembershipChange.status === "ACTION_REQUIRED"
                  ? "Action required"
                  : "Pending",
              currentMembershipName:
                pendingMembershipChange.oldMembership.membershipTypeName,
              scheduledMembershipName:
                pendingMembershipChange.newMembership.membershipTypeName,
              effectiveDateLabel: formatUtcCalendarDate(
                pendingMembershipChange.effectiveAtUtcMilli
              ),
              actionRequiredReason:
                pendingMembershipChange.actionRequiredReason || undefined,
            }
          : undefined
      }
      membershipPlaceholder={membershipPresentation.placeholder}
      disabled={isReadOnly || chamberSettingsLoading}
      errors={membershipAdjustmentErrors}
      onFullTimeEmployeesChange={(value) =>
        updateMembershipAdjustmentDraft({ fullTimeEmployees: value })
      }
      onPartTimeEmployeesChange={(value) =>
        updateMembershipAdjustmentDraft({ partTimeEmployees: value })
      }
      onMembershipTypeChange={(value) =>
        updateMembershipAdjustmentDraft({
          membershipTypeId: value,
        })
      }
      onPaymentScheduleChange={(value) =>
        updateMembershipAdjustmentDraft({
          paymentSchedule: value as ChamberMemberProPaymentScheduleEnum,
        })
      }
      onPaymentMethodChange={(value) =>
        updateMembershipAdjustmentDraft({
          paymentMethod: value as PaymentMethodType,
        })
      }
      onMembershipDiscountChange={(membershipDiscount) =>
        updateMembershipAdjustmentDraft({
          membershipDiscount: {
            ...membershipBillingValue.membershipDiscount,
            ...membershipDiscount,
          },
        })
      }
      onAdditionalCostToggle={() => undefined}
      onBillingResponsibilityChange={(value) =>
        updateMembershipAdjustmentDraft({
          billingResponsibility: value as BillingResponsibilityOption,
        })
      }
      onAutoRenewChange={(checked) =>
        updateMembershipAdjustmentDraft({ autoRenew: checked })
      }
      onEmailConsentChange={(checked) =>
        updateSection("membership", {
          emailCommunicationConsentConfirmed: checked,
        })
      }
    />
  );

  const renderActiveStep = () => {
    if (activeStep === 1) {
      return renderStep1();
    }

    if (activeStep === 2) {
      return renderStep2();
    }

    if (activeStep === 3) {
      return renderStep3();
    }

    return renderStep4();
  };

  const renderConfirmationDialog = () => {
    const modal = confirmationModal;

    if (!modal) {
      return null;
    }

    const modalCopy: Record<
      NonNullable<ConfirmationModal>,
      {
        icon: React.ReactNode;
        iconClass: string;
        title: string;
        copy: React.ReactNode;
        confirm: string;
        confirmClass: string;
        confirmVariant?: "outlined";
      }
    > = {
      resetPassword: {
        icon: <LockResetIcon sx={{ fontSize: 56, color: "white" }} />,
        iconClass: "bg-gray-900",
        title: "Reset Dashboard Password",
        copy: (
          <span className="font-bold">
            Are you sure you want to continue to reset the dashboard password
            for this member?
          </span>
        ),
        confirm: "Reset",
        confirmClass: "#FFFFFF",
        // The reset mock's RESET button is white/outlined with black text.
        confirmVariant: "outlined",
      },
      suspend: {
        icon: <PauseCircleOutlineIcon sx={{ fontSize: 60, color: "white" }} />,
        iconClass: "bg-green-600",
        title: "Membership Status",
        copy: (
          <>
            <span className="block">
              This member account is currently <strong>ACTIVE</strong>.
            </span>
            <span className="mt-6 block">
              Clicking <strong>SUSPEND</strong> will save this member’s profile
              and perks in your Chamber Dashboard, enabling you to reactivate
              them anytime. Their profile will be removed from the Chamber
              Member Pro™ website, and they will lose access to their Member
              Dashboard until their status is changed back to Active.
            </span>
            <span className="mt-6 block font-bold">
              Are you sure you want to suspend this member?
            </span>
          </>
        ),
        confirm: "Suspend",
        confirmClass: "#6B7280",
      },
      activate: {
        icon: <PlayCircleOutlineIcon sx={{ fontSize: 60, color: "white" }} />,
        iconClass: "bg-green-600",
        title: "Activate Member",
        copy: (
          <>
            <span className="block">
              This membership has satisfied its payment and approval
              requirements.
            </span>
            <span className="mt-6 block">
              Activating the member will make their eligible directory profile
              and member benefits available.
            </span>
            <span className="mt-6 block font-bold">
              Are you sure you want to activate this member?
            </span>
          </>
        ),
        confirm: "Activate",
        confirmClass: "#00B83F",
      },
      unsuspend: {
        icon: <PlayCircleOutlineIcon sx={{ fontSize: 60, color: "white" }} />,
        iconClass: "bg-gray-500",
        title: "Membership Status",
        copy: (
          <>
            <span className="block">
              This member account is currently <strong>SUSPENDED</strong>.
            </span>
            <span className="mt-6 block">
              Clicking Activate will restore the company’s member profile,
              membership benefits, and access to the Chamber Member Pro™
              Dashboard.
            </span>
            <span className="mt-6 block font-bold">
              Are you sure you want to unsuspend this member?
            </span>
          </>
        ),
        confirm: "Unsuspend",
        confirmClass: "#00B83F",
      },
      delete: {
        icon: <DeleteOutlineIcon sx={{ fontSize: 60, color: "white" }} />,
        iconClass: "bg-[#EF1B2D]",
        title: "Delete Member",
        copy: (
          <>
            <span className="block">
              Are you sure you want to delete this member?
            </span>
            <span className="mt-6 block">
              Deleting this member will remove them from your active member
              list and they will no longer be visible in your Chamber Website,
              or Chamber Perks App™ directories.
            </span>
            <span className="mt-6 block">
              The member will lose access to their Member Dashboard, and their
              profile will be moved to Past Members in your Chamber Member
              Pro™ Dashboard for record-keeping and viewing only.
            </span>
          </>
        ),
        confirm: "Delete",
        confirmClass: "#EF1B2D",
      },
      restartApplication: {
        icon: <SendOutlinedIcon sx={{ fontSize: 56, color: "white" }} />,
        iconClass: "bg-cpaGold",
        title: "Submit New Company Membership Application",
        copy: "This will open Create Member with this past member's information prefilled so a new membership application can be submitted.",
        confirm: "Continue",
        confirmClass: commonStyles.cpaGold,
      },
    };
    const activeModalCopy = modalCopy[modal];

    return (
      <Dialog
        open
        onClose={() => setConfirmationModal(null)}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: "24px",
            maxWidth: 760,
            p: { xs: 3, md: 5 },
          },
        }}
      >
        <DialogTitle sx={{ p: 0 }}>
          <IconButton
            aria-label="Close member action confirmation"
            onClick={() => setConfirmationModal(null)}
            sx={{
              position: "absolute",
              right: 24,
              top: 24,
              // Mocks show a red circular close X on every confirmation card.
              backgroundColor: "#EF1B2D",
              color: "white",
              "&:hover": {
                backgroundColor: "#D71920",
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, textAlign: "center" }}>
          <div
            className={`mx-auto flex h-28 w-28 items-center justify-center rounded-full ${activeModalCopy.iconClass}`}
          >
            {activeModalCopy.icon}
          </div>
          <h2 className="mt-8 text-[28px] font-bold leading-9 text-black">
            {activeModalCopy.title}
          </h2>
          <p className="mx-auto mt-6 max-w-2xl whitespace-pre-line text-base leading-7 text-black">
            {activeModalCopy.copy}
          </p>
        </DialogContent>
        <DialogActions
          sx={{
            justifyContent: "center",
            gap: 4,
            px: 0,
            pt: 5,
          }}
        >
          <Button
            variant={
              activeModalCopy.confirmVariant === "outlined"
                ? "outlined"
                : "contained"
            }
            startIcon={
              modal === "delete" ? <DeleteOutlineIcon /> : undefined
            }
            disabled={actionLoading}
            onClick={confirmAction}
            sx={
              activeModalCopy.confirmVariant === "outlined"
                ? {
                    backgroundColor: "white",
                    borderColor: "#D1D5DB",
                    borderRadius: "8px",
                    color: "#111827",
                    fontSize: 20,
                    fontWeight: 900,
                    minWidth: 220,
                    py: 1.5,
                    "&:hover": {
                      backgroundColor: "#F9FAFB",
                      borderColor: "#9CA3AF",
                    },
                  }
                : {
                    backgroundColor: activeModalCopy.confirmClass,
                    borderRadius: "8px",
                    fontSize: 20,
                    fontWeight: 900,
                    minWidth: 220,
                    py: 1.5,
                    "&:hover": {
                      backgroundColor: activeModalCopy.confirmClass,
                    },
                  }
            }
          >
            {activeModalCopy.confirm}
          </Button>
          <Button
            variant="contained"
            disabled={actionLoading}
            onClick={() => setConfirmationModal(null)}
            sx={{
              backgroundColor: commonStyles.charcoal,
              borderRadius: "8px",
              fontSize: 20,
              fontWeight: 900,
              minWidth: 220,
              py: 1.5,
              "&:hover": { backgroundColor: "#111827" },
            }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  if (!chamberId || detailLoading) {
    return (
      <div className="flex min-h-[320px] w-full items-center justify-center">
        <div className="flex items-center gap-3 text-black">
          <CircularProgress size={24} />
          <span>Loading member details...</span>
        </div>
      </div>
    );
  }

  if (detailError || !detail || !member) {
    return (
      <div className="w-full pb-10">
        <Button
          type="button"
          variant="text"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(RoutePaths.chamberAdmin_members_manage)}
          sx={{ color: "#111827", fontWeight: 700 }}
        >
          Back To Manage Members
        </Button>
        <Alert severity="error" className="mt-5">
          {detailError?.message || "Could not load this member."}
        </Alert>
      </div>
    );
  }

  return (
    <div className="w-full pb-36">
      <UnsavedChangesPrompt
        when={
          (isDirty || membershipAdjustmentHasChanges) &&
          !updatingMember &&
          !membershipAdjustmentSubmitting
        }
      />

      {statusMessage && (
        <div className="mb-5 w-full">
          <Alert
            severity={statusMessage.severity}
            onClose={() => setStatusMessage(null)}
          >
            {statusMessage.message}
          </Alert>
        </div>
      )}

      {renderStatusPanel()}

      <MemberStepProgress
        activeStep={activeStep}
        ariaLabel="Update member steps"
        onStepClick={goToStep}
      />

      <main className="w-full pt-6">
        <section
          aria-labelledby={`update-member-step-${activeStep}`}
          className="space-y-6"
        >
          <div className="sr-only">
            <h2 id={`update-member-step-${activeStep}`}>
              {memberWorkflowSteps[activeStep - 1].title}
            </h2>
          </div>
          {renderActiveStep()}
        </section>

        <StepFooter
          activeStep={activeStep}
          error={footerError}
          isReadOnly={isReadOnly || membershipAdjustmentEnabled}
          loading={
            updatingMember ||
            actionLoading ||
            membershipAdjustmentSubmitting ||
            membershipChangeInvoiceEmailSending
          }
          onBack={() => goToStep(activeStep - 1)}
          onNext={handleNextStep}
          onSave={saveUpdate}
        />
      </main>

      {renderConfirmationDialog()}

      <Dialog
        open={Boolean(credentialModalMode)}
        onClose={() => setCredentialModalMode(null)}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: "24px",
            p: { xs: 3, md: 5 },
          },
        }}
      >
        {credentialModalMode && (
          <>
            <DialogTitle sx={{ p: 0 }}>
              <IconButton
                aria-label="Close credentials modal"
                onClick={() => setCredentialModalMode(null)}
                sx={{
                  position: "absolute",
                  right: 18,
                  top: 18,
                  backgroundColor: "#EF1B2D",
                  color: "white",
                  height: 30,
                  width: 30,
                  "&:hover": { backgroundColor: "#D71920" },
                }}
              >
                <CloseIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </DialogTitle>
            <DialogContent sx={{ p: 0 }}>
              <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-[#05B93D]">
                <CheckCircleOutlineIcon sx={{ fontSize: 52, color: "white" }} />
              </div>
              {credentialModalMode === "resetPassword" ? (
                <h2 className="mt-7 text-center text-xl font-bold text-black">
                  New Temporary Password Generated for
                  <br />
                  {formState.directory.companyName}
                </h2>
              ) : (
                <h2 className="mt-7 text-center text-xl font-bold uppercase text-black">
                  Send Member Login Credentials
                </h2>
              )}
              {credentialModalMode === "sendCredentials" && (
                <p className="mx-auto mt-7 max-w-3xl text-sm leading-6 text-black">
                  Send the member their member dashboard credentials using one
                  of the two available options. After sending, monitor their
                  login status under Manage Members, and if they have not
                  accessed their dashboard, follow up to confirm receipt, ask
                  them to check their spam folder, and verify that their
                  Administrator Contact details are accurate before resending.
                </p>
              )}
              <div className="mx-auto mt-8 max-w-3xl rounded-lg border border-black p-6">
                <div>
                  <h3 className="text-base font-bold text-black">
                    {credentialModalMode === "resetPassword"
                      ? "Manually Send Password Reset Instructions:"
                      : "Manually Email Member Credentials:"}
                  </h3>
                  {credentialModalMode === "resetPassword" ? (
                    <p className="mt-4 text-sm leading-6 text-black">
                      Click <strong>“DOWNLOAD CREDENTIALS”</strong> to download
                      a CSV file for{" "}
                      {formState.directory.companyName || "this member"} NEW
                      Member Dashboard Temporary Password Credentials.
                    </p>
                  ) : (
                    <p className="mt-4 text-sm leading-6 text-black">
                      Click <strong>“DOWNLOAD CREDENTIALS”</strong> to download
                      a CSV file containing{" "}
                      {formState.directory.companyName || "this member"}’s
                      Member Dashboard login details (username email and
                      temporary password), then include this information in
                      your email to the member.
                    </p>
                  )}
                  <p className="mt-4 text-sm leading-6 text-black">
                    Click <strong>“PREVIEW EMAIL”</strong> to see a template
                    email you can use to send these credentials manually to{" "}
                    {memberAdminName || "the Company Administrator Contact"}{" "}
                    using your preferred email method.
                  </p>
                  <div className="mt-6 flex flex-wrap gap-3">
                    <Button
                      variant="contained"
                      startIcon={<DownloadOutlinedIcon />}
                      disabled={generatingCredentials}
                      onClick={downloadCredentials}
                      sx={{
                        backgroundColor: cpaGold,
                        fontWeight: 900,
                        "&:hover": { backgroundColor: cpaGoldDark },
                      }}
                    >
                      Download Credentials
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={<PreviewOutlinedIcon />}
                      onClick={() => setPreviewEmailMode(credentialModalMode)}
                      sx={{
                        backgroundColor: commonStyles.charcoal,
                        fontWeight: 900,
                        "&:hover": { backgroundColor: "#111827" },
                      }}
                    >
                      Preview Email
                    </Button>
                  </div>
                </div>
                {/* No divider between the Manual and Automatic sections —
                    the credential-modal mocks show whitespace only. */}
                <div className="mt-8 pt-6">
                  <h3 className="text-base font-bold text-black">
                    {credentialModalMode === "resetPassword"
                      ? "Automatically Email Password Reset Instructions:"
                      : "Automatically Email Member Credentials:"}
                  </h3>
                  {credentialModalMode === "resetPassword" ? (
                    <p className="mt-4 text-sm leading-6 text-black">
                      Click <strong>“SEND AUTO EMAIL”</strong>, and the Preview
                      Email will automatically be sent from
                      noreply@chamberperksapp.com to{" "}
                      {memberAdminEmail || "the Company Administrator email"}{" "}
                      with instructions to reset the password credentials for{" "}
                      {formState.directory.companyName || "this member"}.
                    </p>
                  ) : (
                    <p className="mt-4 text-sm leading-6 text-black">
                      Click <strong>“SEND AUTO EMAIL,”</strong> and the Preview
                      Email will automatically be sent from
                      hello@chamberperksapp.com to{" "}
                      {memberAdminEmail || "the Company Administrator email"}{" "}
                      with instructions to reset the temporary password for{" "}
                      {formState.directory.companyName || "this member"}.
                    </p>
                  )}
                  <Button
                    variant="contained"
                    startIcon={<SendOutlinedIcon />}
                    disabled={sendingCredentials}
                    onClick={sendCredentialsEmail}
                    sx={{
                      mt: 4,
                      backgroundColor: "#00B83F",
                      fontWeight: 900,
                      "&:hover": { backgroundColor: "#009934" },
                    }}
                  >
                    Send Auto Email
                  </Button>
                </div>
              </div>
            </DialogContent>
          </>
        )}
      </Dialog>

      <Dialog
        open={Boolean(previewEmailMode)}
        onClose={() => setPreviewEmailMode(null)}
        fullWidth
        maxWidth="md"
        PaperProps={{ sx: { borderRadius: "20px" } }}
      >
        <DialogTitle className="relative text-center">
          <IconButton
            aria-label="Back to credentials modal"
            onClick={() => setPreviewEmailMode(null)}
            sx={{ position: "absolute", left: 16, top: 12, color: "#111827" }}
          >
            <ArrowBackIcon />
          </IconButton>
          <span className="text-xl font-bold text-black">Preview Email</span>
          <IconButton
            aria-label="Close preview email"
            onClick={() => setPreviewEmailMode(null)}
            sx={{
              position: "absolute",
              right: 16,
              top: 12,
              backgroundColor: "#EF1B2D",
              color: "white",
              height: 30,
              width: 30,
              "&:hover": { backgroundColor: "#D71920" },
            }}
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {previewEmailLoading ? (
            <div className="flex items-center gap-3 py-10 text-black">
              <CircularProgress size={22} />
              Loading email preview...
            </div>
          ) : (
            <div
              className="prose max-w-none rounded-lg border border-dashed border-gray-500 p-5 text-black"
              dangerouslySetInnerHTML={{
                __html:
                  previewEmailData?.getSampleLoginInfoEmail.emailHtml ||
                  "<p>No email preview available.</p>",
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={Boolean(successDialog)}
        onClose={() => {
          if (!successDialog?.actionLoading) {
            setSuccessDialog(null);
          }
        }}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: "24px",
            p: { xs: 3, md: 5 },
          },
        }}
      >
        {successDialog && (
          <DialogContent sx={{ textAlign: "center" }}>
            {/* Red circular close per the mock's success pop-ups. */}
            <IconButton
              aria-label="Close success confirmation"
              disabled={successDialog.actionLoading}
              onClick={() => setSuccessDialog(null)}
              sx={{
                position: "absolute",
                right: 18,
                top: 18,
                backgroundColor: "#EF1B2D",
                color: "white",
                height: 32,
                width: 32,
                "&:hover": { backgroundColor: "#D71920" },
              }}
            >
              <CloseIcon sx={{ fontSize: 20 }} />
            </IconButton>
            <div
              className={`mx-auto flex h-24 w-24 items-center justify-center rounded-full ${
                successDialog.tone === "green"
                  ? "bg-green-600"
                  : "bg-[#EF1B2D]"
              }`}
            >
              <CheckCircleOutlineIcon sx={{ fontSize: 60, color: "white" }} />
            </div>
            <h2 className="mt-8 text-[28px] font-bold leading-9 text-black">
              {successDialog.title}
            </h2>
            {successDialog.body && (
              <div className="mx-auto mt-6 max-w-md text-base leading-7 text-black">
                {successDialog.body}
              </div>
            )}
            {successDialog.actionError && (
              <Alert severity="error" className="mt-5 text-left">
                {successDialog.actionError}
              </Alert>
            )}
            {(successDialog.actionLabel ||
              successDialog.secondaryActionLabel) && (
              <div className="mt-5 flex flex-col justify-center gap-3 sm:flex-row">
                {successDialog.actionLabel && successDialog.onAction && (
                  <Button
                    variant="contained"
                    disabled={successDialog.actionLoading}
                    onClick={successDialog.onAction}
                    sx={{
                      backgroundColor: cpaGold,
                      fontSize: 18,
                      fontWeight: 900,
                      minWidth: 180,
                      "&:hover": { backgroundColor: cpaGoldDark },
                    }}
                  >
                    {successDialog.actionLoading
                      ? "Sending Invoice..."
                      : successDialog.actionLabel}
                  </Button>
                )}
                {successDialog.secondaryActionLabel &&
                  successDialog.onSecondaryAction && (
                    <Button
                      variant="contained"
                      disabled={successDialog.actionLoading}
                      onClick={successDialog.onSecondaryAction}
                      sx={{
                        backgroundColor: commonStyles.charcoal,
                        fontSize: 18,
                        fontWeight: 900,
                        minWidth: 180,
                        "&:hover": { backgroundColor: "#111827" },
                      }}
                    >
                      {successDialog.secondaryActionLabel}
                    </Button>
                  )}
              </div>
            )}
          </DialogContent>
        )}
      </Dialog>

      {/* Affinity Program Partner save confirmation (PROCEED/CANCEL) per the
          step 3 spec annotation. */}
      <Dialog
        open={affinityConfirmOpen}
        onClose={() => setAffinityConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <IconButton
          onClick={() => setAffinityConfirmOpen(false)}
          aria-label="Close affinity program confirmation"
          sx={{
            position: "absolute",
            right: 12,
            top: 12,
            backgroundColor: "#EF4444",
            color: "white",
            "&:hover": { backgroundColor: "#DC2626" },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
        <DialogTitle className="text-center">
          <div className="mx-auto mb-4 mt-2 flex h-20 w-20 items-center justify-center rounded-full bg-red-600 text-white">
            <ErrorOutlineIcon sx={{ fontSize: 54 }} />
          </div>
          <span className="block text-center text-lg font-bold text-gray-950">
            Affinity Program Partner
          </span>
        </DialogTitle>
        <DialogContent>
          <p className="mx-auto max-w-xs text-center text-sm leading-6 text-black">
            Are you sure you want to add this Affinity Program details to this
            member?
          </p>
        </DialogContent>
        <DialogActions className="justify-center gap-4 px-6 pb-6">
          <Button
            type="button"
            variant="contained"
            onClick={handleConfirmAffinityEnrollment}
            sx={{
              backgroundColor: cpaGold,
              color: "white",
              fontWeight: 800,
              minWidth: 140,
              "&:hover": { backgroundColor: cpaGoldDark },
            }}
          >
            Proceed
          </Button>
          <Button
            type="button"
            variant="contained"
            onClick={() => setAffinityConfirmOpen(false)}
            sx={{
              backgroundColor: commonStyles.charcoal,
              color: "white",
              fontWeight: 800,
              minWidth: 140,
              "&:hover": { backgroundColor: "#111827" },
            }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* "Update {program name}" modal opened from the Enrolled Affinity
          Programs table's Edit button. */}
      <Dialog
        open={!!editingAffinityEnrollment}
        onClose={() => setEditingAffinityEnrollment(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle className="flex items-center justify-between gap-4">
          <span className="text-lg font-bold text-gray-950">
            Update {editingAffinityEnrollment?.affinityProgramPartnerName || ""}
          </span>
          <Button
            type="button"
            onClick={() => setEditingAffinityEnrollment(null)}
            sx={{ minWidth: 0, color: "#6B7280" }}
            aria-label="Close update affinity program modal"
          >
            <CloseIcon />
          </Button>
        </DialogTitle>
        <DialogContent>
          {editingAffinityEnrollment && (
            <div className="pt-2">
              <RadioChoiceGroup
                label="Billing Options"
                options={affinityBillingOptions}
                value={editingAffinityEnrollment.billingOption}
                onChange={(value) =>
                  handleUpdateEditingAffinityEnrollment("billingOption", value)
                }
                columns="wide"
                renderOptionLabel={renderAffinityBillingOptionLabel}
              />
              <FormSelectField
                id="edit-affinity-status"
                label="Status"
                value={editingAffinityEnrollment.status}
                onChange={(value) =>
                  handleUpdateEditingAffinityEnrollment("status", value)
                }
                options={affinityEnrollmentStatusOptions.map((option) => ({
                  value: option,
                  label: <AffinityEnrollmentStatusIndicator status={option} />,
                }))}
                placeholder="Select status"
                selectRenderValue={(value) =>
                  value ? (
                    <AffinityEnrollmentStatusIndicator status={value} />
                  ) : (
                    "Select status"
                  )
                }
              />
              <FormTextField
                id="edit-affinity-notes"
                label="Notes"
                value={editingAffinityEnrollment.notes}
                onChange={(value) =>
                  handleUpdateEditingAffinityEnrollment("notes", value)
                }
                placeholder="Enter Notes"
                multiline
                rows={5}
              />
            </div>
          )}
        </DialogContent>
        <DialogActions className="px-6 pb-5">
          {/* Cancel is white/outlined and Update gold per the Update modal
              annotation in the step 3 spec. */}
          <Button
            type="button"
            variant="outlined"
            onClick={() => setEditingAffinityEnrollment(null)}
            sx={{
              borderColor: "#D1D5DB",
              color: "#111827",
              fontWeight: 800,
              minWidth: 120,
              textTransform: "none",
              "&:hover": {
                backgroundColor: "#F9FAFB",
                borderColor: "#9CA3AF",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="contained"
            onClick={handleSaveEditingAffinityEnrollment}
            sx={{ ...wizardGoldButtonSx, minWidth: 120 }}
          >
            Update
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default UpdateMember;
